/**
 * Chat store — conversations, messages, WebSocket events, and tool call state.
 *
 * The WebSocket is a single multiplexed connection (opened once after auth).
 * Switching conversations sends a ``subscribe`` message instead of
 * disconnecting / reconnecting.
 */

import { useMemo } from "react";
import { create } from "zustand";
import type {
  Conversation,
  ConversationDetail,
  Message,
  MessagesPage,
  ContextBreakdown,
  ModelTokenUsage,
  ReportConversation,
  SubagentInfo,
  SubagentRun,
  ToolCallState,
  WsServerMessage,
} from "@/api/types";
import { fetchReportsConversations } from "@/api/reports";
import { fetchSubagentRuns } from "@/api/subagentRuns";
import { parseUTC } from "@/api/types";
import { apiFetch, isNativeHost } from "@/api/client";
import { useShortlistStore } from "@/stores/shortlistStore";
import { useNavStore } from "@/stores/navStore";
import { useAuthStore } from "@/stores/authStore";
import { useToastStore } from "@/components/common/Toast";
import { webNotify } from "@/api/webNotify";
import { getProvider } from "@/components/common/ProviderLogos";
import * as ws from "@/api/ws";
import { computeContextPercent, getContextLimit } from "@/api/modelLimits";

/** Right-panel tabs. Named so the state field and both actions can't drift
 *  apart — adding a tab used to mean editing the same union in three places. */
export type RightPanelTab = "shortlist" | "files" | "plan" | "context" | "subagents";

type WsStatus = "connecting" | "connected" | "disconnected" | "error" | "session_expired";

// ─── Deferred running-state re-check (stuck-"Working" fix) ───────────────
// The anti-flap guard in the ``conversation_upsert`` handler ignores a
// ``running=false`` row within 8s of a local send (the row can't reflect the
// send yet). But row upserts are EDGE-triggered: when a remote-dispatched turn
// finishes in <8s, its final clearing edge falls inside the guard window and
// nothing ever re-delivers it — the footer showed "Working" forever (found
// live: a 5s cloud turn). So whenever a not-running row is suppressed, re-check
// the LEVEL (the row already merged into ``conversations``) after the window.
const runningRecheckTimers = new Map<string, number>();
function scheduleRunningRecheck(convId: string) {
  const prev = runningRecheckTimers.get(convId);
  if (prev) window.clearTimeout(prev);
  runningRecheckTimers.set(convId, window.setTimeout(() => {
    runningRecheckTimers.delete(convId);
    const s = useChatStore.getState();
    if (s.activeConversationId !== convId || !s.isAgentRunning) return;
    if (Date.now() - (s.lastSendAt[convId] || 0) < 8000) {
      scheduleRunningRecheck(convId); // sent again meanwhile — wait it out
      return;
    }
    // Look in BOTH lists: an admin's open chat lives in ``teamConversations``,
    // and searching only ``conversations`` meant this safety net could never
    // fire for the one viewer with no other way back to the truth.
    const row =
      s.conversations.find((c) => c.id === convId) ??
      s.teamConversations.find((c) => c.id === convId);
    if (row && !row.agent_running) {
      // Deliberately NOT gated on ``runningTasks`` any more. This is the safety
      // net for the case where the durable row says idle but the turn tail
      // never emitted ``task_status:false``; bailing on a local entry here
      // (as it used to) made the net useless in exactly that case — the only
      // one it needs to catch. 8s past the last send with an idle row, the
      // local entry is stale, so drop it too or the sidebar keeps spinning.
      const runningTasks = new Set(s.runningTasks);
      runningTasks.delete(convId);
      useChatStore.setState({ isAgentRunning: false, runningTasks });
    }
  }, 8500));
}

/**
 * Merge a pushed conversation row into whichever sidebar list owns it.
 *
 * One frame now reaches the owner AND any authorized cross-user viewer
 * (ADR-0009), so the row has to be routed rather than blindly merged into
 * ``conversations`` — otherwise a colleague's chat would surface in "Your
 * Chats". ``owner_id`` comes from the broadcast's own scope, so it always
 * agrees with why the frame was delivered.
 *
 * Routing exists in exactly this one place: the whole reason an admin's rows
 * went stale is that convergence lived on a path only the owner's list took.
 * A handler that patches one list and forgets the other recreates that bug, so
 * both lists are only ever updated through here.
 *
 * An EXISTING row is always patched wherever it already lives, without
 * consulting identity at all. That matters: identity comes from the auth store,
 * and a frame arriving before it settles would otherwise be misrouted — and the
 * frames that matter most here are the clearing ones for a chat the viewer is
 * already staring at. Only a row we have never seen needs routing, and that is
 * where ``owner_id`` is used.
 *
 * New foreign rows are prepended only when they pass the active owner filter —
 * a live row for a filtered-out user must not appear in a list the viewer has
 * narrowed. A frame with no ``owner_id`` (older backend) is treated as the
 * viewer's own, preserving the previous behaviour.
 */
function mergeConversationRow(
  s: ChatState,
  conversation: Conversation,
  ownerId: string | undefined,
): Partial<ChatState> {
  const teamIdx = s.teamConversations.findIndex((c) => c.id === conversation.id);
  if (teamIdx >= 0) {
    return {
      // Owner identity is immutable and absent from the broadcast row, so it is
      // re-asserted from the row we already have — otherwise the merge would
      // blank the owner badge on every live update.
      teamConversations: s.teamConversations.map((c, i) =>
        i === teamIdx
          ? {
              ...c,
              ...conversation,
              owner_id: c.owner_id,
              owner_email: c.owner_email,
              owner_name: c.owner_name,
            }
          : c,
      ),
    };
  }

  const ownIdx = s.conversations.findIndex((c) => c.id === conversation.id);
  if (ownIdx >= 0) {
    return {
      conversations: s.conversations.map((c, i) =>
        i === ownIdx ? { ...c, ...conversation } : c,
      ),
    };
  }

  // Never seen before — now identity decides which list it belongs in.
  const selfId = useAuthStore.getState().user?.id;
  if (!ownerId || !selfId || ownerId === selfId) {
    return { conversations: [conversation, ...s.conversations] };
  }
  // Foreign: only surface it if the viewer isn't filtered to someone else. The
  // list row carries owner display fields when the backend includes them;
  // otherwise the badge stays blank until the next fetch.
  if (s.teamOwnerFilter && s.teamOwnerFilter !== ownerId) return {};
  const row: ReportConversation = {
    ...conversation,
    owner_id: ownerId,
    owner_email: conversation.owner_email ?? "",
    owner_name: conversation.owner_name ?? "",
  };
  return { teamConversations: [row, ...s.teamConversations] };
}

/** First text-block string in a message's ``content_json`` (trimmed), or "".
 *  Used to reconcile an optimistic user bubble with its synced persisted twin. */
function firstText(content_json: string): string {
  try {
    const blocks = JSON.parse(content_json);
    if (!Array.isArray(blocks)) return "";
    const t = blocks.find((b) => b?.type === "text")?.text;
    return typeof t === "string" ? t.trim() : "";
  } catch {
    return "";
  }
}

/**
 * Build an optimistic ``model_switch`` marker message for instant in-session
 * feedback. The backend writes the durable copy (PATCH handler for manual
 * picks, ``_announce_model_switch`` for automatic ones); this transient copy
 * just renders immediately so the user sees the switch at the moment it
 * happens. On reload the persisted row replaces it (a full re-hydrate, so no
 * duplicate). ``id``/``created_at`` are taken from the backend event when
 * available so the live row matches its persisted twin.
 */
function buildModelSwitchMessage(opts: {
  model: string;
  source: "manual" | "auto" | "observer" | "agent";
  by: string;
  provider?: string | null;
  reason?: string | null;
  id?: string;
  created_at?: string;
}): Message {
  const block = {
    type: "model_switch" as const,
    model: opts.model,
    provider: opts.provider ?? getProvider(opts.model),
    source: opts.source,
    by: opts.by,
    ...(opts.reason ? { reason: opts.reason } : {}),
  };
  return {
    id: opts.id ?? `switch-${Date.now()}`,
    role: "model_switch",
    content_json: JSON.stringify([block]),
    created_at: opts.created_at ?? new Date().toISOString(),
    model: opts.model,
  };
}

/**
 * Build an ``env_switch`` marker message for instant in-session feedback when a
 * conversation is moved between execution environments. The backend writes the
 * durable copy (transfer endpoint) and pushes ``env_switched``; this transient
 * copy renders immediately. ``id``/``created_at`` come from the backend event so
 * the live row matches its persisted twin (a reload re-hydrate replaces it with
 * no duplicate). Mirrors ``buildModelSwitchMessage``.
 */
function buildEnvSwitchMessage(opts: {
  from: string;
  to: string;
  from_label: string;
  to_label: string;
  by: string;
  id: string;
  created_at?: string | null;
}): Message {
  const block = {
    type: "env_switch" as const,
    from: opts.from,
    to: opts.to,
    from_label: opts.from_label,
    to_label: opts.to_label,
    by: opts.by,
  };
  return {
    id: opts.id,
    role: "env_switch",
    content_json: JSON.stringify([block]),
    created_at: opts.created_at ?? new Date().toISOString(),
  };
}

/**
 * Page size for paginated message loads — matches the backend's
 * ``DEFAULT_MESSAGE_PAGE_SIZE`` so the initial detail fetch and any
 * subsequent ``/messages?before=`` calls deliver the same chunk size.
 */
const MESSAGE_PAGE_SIZE = 100;

/**
 * In-memory window cap. Once the loaded message array grows past this
 * — typically during a long agent run — the store trims the oldest
 * portion back down to ``MESSAGE_WINDOW_PRUNE_TO`` so React reconciles
 * over a bounded array and the various ``useMemo``s in MessageList
 * stay cheap. The tradeoff: if the user scrolls back up after a prune
 * they'll incur a fetch round-trip to re-hydrate the dropped page.
 */
const MESSAGE_WINDOW_MAX = 500;
const MESSAGE_WINDOW_PRUNE_TO = 300;

/**
 * Minimum visible duration for the conversation-switch shimmer. The
 * detail endpoint can return in <50 ms on a same-machine dev setup,
 * which makes the loading→content transition look like a flash rather
 * than a load. Padding to this floor smooths it out without adding
 * meaningful latency to remote deployments (where real RTT already
 * exceeds it).
 */
const MIN_HISTORY_LOAD_MS = 500;

interface DesktopNotifyOptions {
  title: string;
  message: string;
  playSound?: boolean;
  conversationId?: string;
  /** Used by the backend to set urgency + match toast styling. */
  intent?: "response" | "shortlist" | "question" | "approval" | "error";
}

async function triggerDesktopNotify(opts: DesktopNotifyOptions): Promise<void> {
  // Browser (cloud web): the desktop endpoint plays inside the SERVER
  // container — chime + Web Notification must happen client-side instead.
  if (!isNativeHost()) {
    webNotify({
      title: opts.title,
      message: opts.message,
      playSound: opts.playSound,
      intent: opts.intent,
      onClick: opts.conversationId
        ? () => void useChatStore.getState().openConversation(opts.conversationId!)
        : undefined,
    });
    return;
  }
  try {
    await fetch("/api/desktop/notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: opts.title,
        message: opts.message,
        play_sound: opts.playSound ?? true,
        conversation_id: opts.conversationId,
        intent: opts.intent,
      }),
    });
  } catch {
    // Desktop notifications are best-effort only.
  }
}

/** Best-effort focus of the native window — used when the user clicks
 *  a deep-link toast / OS notification and the window may be minimised. */
async function raiseAppWindow(): Promise<void> {
  try {
    await fetch("/api/instance/focus", { method: "POST" });
  } catch {
    /* no native window available (browser mode) — nothing to raise */
  }
}

export interface AttachedFile {
  name: string;
  path: string; // Real filesystem path — from pywebview native dialog or uploaded to backend
  /**
   * Set only while an attachment is STAGED but not yet uploaded, which is the
   * Home composer's normal state: there is no conversation to upload into
   * until the user actually submits, so the bytes are held client-side and
   * ``path`` stays "". ``HomeChatInput`` uploads these after
   * ``createConversation`` and replaces each entry with its real path.
   *
   * Uploading at attach time — as Home used to — has to name SOME conversation,
   * and the only one available is ``activeConversationId``: the previously open
   * chat, which on a read-only admin view belongs to a different user entirely
   * (2026-07-29). Staging removes the choice rather than making it safer.
   */
  file?: File;
}

/** One item in the agent's TODO list (emitted via the TodoWrite tool). */
export interface AgentTodo {
  content: string;
  status: "pending" | "in_progress" | "completed";
  activeForm?: string;
}

interface ChatState {
  conversations: Conversation[];
  /** The admin "Others" tab's rows (ADR-0009) — other people's conversations
   *  this viewer is authorized to read. Lives HERE rather than in Sidebar-local
   *  state so the same WS handlers that converge ``conversations`` converge
   *  these too: while it was component state, fetched only on tab activation,
   *  an admin's view of someone else's chat could not change without a reload —
   *  a stale "Working" row had no path back to the truth. */
  teamConversations: ReportConversation[];
  /** Owner filter backing ``teamConversations`` (empty = all users). Kept beside
   *  the rows so a live upsert for a filtered-out owner is not prepended. */
  teamOwnerFilter: string;
  activeConversationId: string | null;
  // Portable sessions (Phase C): set when a turn was refused here because the
  // conversation runs in another environment (`location`); the composer surfaces
  // a "runs on X — bring it here" prompt. Cleared on nav / successful send.
  executionElsewhere: { conversationId: string; location: string } | null;
  /**
   * Frozen ``last_read_at`` captured at the moment the active
   * conversation was opened. Drives the in-chat "Unread" divider —
   * any message with ``created_at`` strictly greater than this cutoff
   * sits below the divider. Stays put while the user reads, even as
   * mark-read calls keep advancing the server-side timestamp, so the
   * divider doesn't slide out from under them.
   */
  unreadCutoff: string | null;
  messages: Message[];
  toolCalls: Map<string, ToolCallState>;
  /** Live sub-agents (Task tool) keyed by tool_use_id, driving the "Working"
   *  indicator's count + activity lines. Only holds running entries — a
   *  terminal ``subagent_progress`` frame removes them; cleared at turn
   *  boundaries. See ``useRunningSubagents``. */
  subagents: Map<string, SubagentInfo>;
  wsStatus: WsStatus;
  isAgentRunning: boolean;
  /** Set when the ACTIVE conversation belongs to one of the viewer's reports
   *  (ADR-0009): the viewer is a manager working in the owner's context.
   *  Drives the persistent banner in ChatView. Null on the user's own chats. */
  viewingAsManager: { ownerName: string; ownerEmail: string } | null;
  /** Epoch ms each conversation's current agent turn began, keyed by
   *  conversation id — the working-indicator elapsed label reads its own
   *  conversation's entry. Maintained centrally by the subscription at the end
   *  of this file: stamped when a conversation *starts* running and cleared when
   *  it stops. Keyed per-conversation (not a single global) so switching chats
   *  mid-turn preserves the anchor instead of resetting the elapsed clock to 0. */
  turnStartedAt: Record<string, number>;
  /** Durable sub-agent runs for the OPEN conversation (ADR-0019), newest first.
   *
   *  Distinct from ``subagents``, which is the transient live map driving the
   *  "Working · N sub-agents" indicator and is cleared at every turn boundary.
   *  These are persisted rows: they survive reload, restart and environment
   *  change, and they are what the Sub-agents panel renders. Refreshed by the
   *  ``subagents_changed`` WS frame, never polled. */
  subagentRuns: SubagentRun[];
  /** Transient live draft of the streaming assistant text block, shown in the
   *  footer until the persisted ``assistant_text`` replaces it. Cosmetic. */
  streamingText: string;
  /** Content-block index feeding ``streamingText``; a delta for a new block resets the draft. */
  streamingBlock: number | null;
  /** Id of the message whose content was just shown via the streaming draft.
   *  The list renders it without the entry animation so it swaps in seamlessly
   *  instead of replaying a bubble-up over already-visible text. */
  lastStreamedMessageId: string | null;
  /** Live extended-thinking transcript for the current step, shown in a
   *  collapsible panel. Cosmetic; cleared with the turn, never persisted. */
  thinkingText: string;
  /** Thinking panel expanded state; auto-collapses when the answer starts. */
  thinkingExpanded: boolean;
  /** True once the answer/tool begins — header reads "Thought" not "Thinking…". */
  thinkingDone: boolean;
  /** Epoch ms the current thinking began, for the elapsed label. */
  thinkingStartedAt: number | null;
  /** Set of conversation IDs that currently have a running agent task. */
  runningTasks: Set<string>;
  pendingApproval: { action: string; description: string; request_id?: string } | null;
  pendingQuestions: Array<{
    question: string;
    header: string;
    multiSelect: boolean;
    options: Array<{ label: string; description: string }>;
  }> | null;
  /** request_id from the server-emitted user_question event; sent back with the answer. */
  pendingQuestionRequestId: string | null;
  /** Per-conversation timestamp of the last local send — guards the run
   *  indicator against a stale `task_status{running:false}` that lands just
   *  after a send (e.g. the pre-send subscribe on a new chat). */
  lastSendAt: Record<string, number>;
  /** The row's `active_turn_started_at` (server clock, ms) captured at the
   *  moment of the last local send. A not-running row can only count as "our
   *  turn finished" when its start stamp is NEWER than this — a row bumped
   *  between the send and `begin_turn` (mark-read, lease-owner stamp) still
   *  carries the PREVIOUS turn's start and must not blank the indicator.
   *  Same-field server-clock comparison, so client↔server skew is irrelevant. */
  preSendTurnStart: Record<string, number>;
  /** Id of the live cross-device sync toast, so progress ticks update one card. */
  syncToastId: string | null;
  attachedFiles: AttachedFile[];
  /**
   * Draft text for the new-chat (Home) composer, held in the store rather
   * than in ``HomeChatInput`` local state. The composer is rendered in two
   * structurally different branches of ``HomeView`` (centered empty-state vs
   * bottom-pinned populated-state), so when a background widget refresh flips
   * the layout, React unmounts one instance and mounts the other — which would
   * wipe component-local text mid-typing. Keeping the draft here lets it
   * survive that remount. Cleared on submit and on landing on Home (see
   * ``HomeView``), so each fresh visit still starts empty.
   */
  homeDraft: string;
  setHomeDraft: (text: string) => void;
  /**
   * Seed text from an ``omniwerk://`` deep link, waiting to be dropped into the
   * Home composer. Set by the ``open_deeplink`` handler, consumed by HomeView
   * (which otherwise clears ``homeDraft`` on mount). A separate field — not a
   * direct ``setHomeDraft`` — so HomeView's mount-clear can't race it.
   */
  pendingHomeSeed: string | null;
  setPendingHomeSeed: (text: string | null) => void;
  /** Evaluator quality check state — shown as a tool-call-like block. */
  evaluatorState: {
    status: "running" | "passed" | "failed";
    issues?: Array<{ severity: string; program: string; issue: string }>;
    rerunning?: boolean;
    startedAt: number;
  } | null;
  /** Per-conversation queued messages, mirrored from the server-side queue.
   *  Each entry carries the ``client_msg_id`` the send assigned so the
   *  ``message_dequeued`` echo can promote the exact optimistic bubble to a
   *  real one. Source of truth is the backend (see ``handle_message``); this
   *  is a display mirror, re-hydrated from ``task_status.queued`` on
   *  reconnect, so it is deliberately NOT persisted to localStorage. */
  queuedMessages: Record<string, Array<{ id: string; text: string }>>;
  /** Artifacts for the current conversation. ``file_path`` is present
   *  on artifacts hydrated from the conversation payload (used by the
   *  Files panel for existence checks + basename de-dup) and absent on
   *  artifacts that arrive via live streaming events. */
  artifacts: Array<{ id: string; filename: string; file_type: string; file_path?: string; created_at: string }>;
  /** Generative UI: live HTML content keyed by genui_id, updated in-place on genui_update */
  genuiHtml: Map<string, string>;
  /** Agent's live TODO list — updated on each TodoWrite invocation.
   *  Surfaced in the right panel's "Plan" tab (see RightPanel). */
  todos: AgentTodo[];
  /** Unified right panel state */
  rightPanelOpen: boolean;
  rightPanelTab: RightPanelTab;
  openRightPanel: (tab?: RightPanelTab) => void;
  closeRightPanel: () => void;
  setRightPanelTab: (tab: RightPanelTab) => void;
  /** @internal True once history has loaded for the active conversation. */
  _historyLoaded: boolean;
  /**
   * @internal Monotonic counter bumped whenever the user submits a new
   * message (direct send or queue-flush). MessageList subscribes to this
   * and force-scrolls to the bottom on each tick — sending implies the
   * user wants to see the response, so we override ``followOutput``'s
   * "only follow if already at bottom" behaviour for this case only.
   */
  _scrollToBottomTick: number;
  /**
   * Whether the server has older messages than what we currently hold.
   * Drives the "scroll up to load more" affordance in MessageList.
   */
  hasMoreOlder: boolean;
  /**
   * Cursor (``created_at``) of the oldest message in memory. Sent as
   * ``?before=`` to ``GET /conversations/{id}/messages``. ``null`` means
   * we've loaded back to the start of the conversation.
   */
  oldestLoaded: string | null;
  /** True while an upward-paging fetch is in flight — used to debounce. */
  loadingOlder: boolean;
  /**
   * Forward-pagination siblings of ``hasMoreOlder`` / ``oldestLoaded`` /
   * ``loadingOlder``. Only relevant after an *anchored* open (a search "jump
   * to match") lands the window mid-conversation with newer messages still
   * unloaded below; a normal open already holds the newest message, so
   * ``hasMoreNewer`` is false and ``loadNewerMessages`` is a no-op.
   */
  hasMoreNewer: boolean;
  /** Cursor (``created_at``) of the newest message in memory. Sent as
   *  ``?after=`` to page down. ``null`` until an anchored open sets it. */
  newestLoaded: string | null;
  /** True while a downward-paging fetch is in flight. */
  loadingNewer: boolean;
  /**
   * Message id the view should jump to on the next open — set by an anchored
   * ``openConversation`` (search "jump to match"), read once by ``MessageList``
   * at mount to build its scroll target, then cleared via
   * ``consumeScrollTarget``. Null for an ordinary open.
   */
  pendingScrollTargetId: string | null;
  /**
   * Message id to flash a ring around (the jumped-to message). Set alongside
   * ``pendingScrollTargetId`` and cleared by a timer after the flash plays.
   */
  highlightedMessageId: string | null;
  /**
   * Whether the message list is currently anchored to the bottom. Updated
   * by ``MessageList`` via Virtuoso's ``atBottomStateChange``. Pruning
   * only fires while at the bottom so the user's reading position is
   * never disturbed.
   */
  atBottom: boolean;
  /**
   * Whether to show the "you've reached the top of this chat" marker.
   * Set by ``MessageList`` only when the user has scrolled to the top of a
   * conversation that (a) is actually scrollable and (b) has no more history
   * to page in (``hasMoreOlder`` is false). Kept in the store — rather than
   * in ``MessageList`` local state — because the marker is rendered by the
   * Virtuoso ``Header`` slot, which must stay a stable store-reading
   * component (see ``ListHeader``) and so can't receive it as a prop.
   */
  historyTopReached: boolean;
  /**
   * Context-window usage for the active conversation. Driven by
   * ``ResultMessage.usage`` from the backend — ``contextTokens`` is the
   * size of the last turn's prompt (input + cache reads), ``contextLimit``
   * is the selected model's ceiling, ``contextPercent`` is the
   * pre-computed bar value (0-100) the UI reads.
   */
  contextTokens: number;
  contextLimit: number;
  contextPercent: number;
  contextModel: string | null;
  /**
   * Cumulative token counters for the active conversation, surfaced
   * in the chat footer beside the model selector. Hydrated from the
   * conversation row on open and incremented on every ``result``.
   */
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCacheReadTokens: number;
  totalCacheCreationTokens: number;
  /**
   * Per-model / per-provider breakdown of the cumulative token counters
   * above, keyed by model id (e.g. ``claude-opus-4-8`` →
   * {provider, input, output, cache…}). Lets the TokenCounter show which
   * model and provider (anthropic / openrouter / …) the tokens went to.
   * Hydrated from the conversation row on open, *set* from the authoritative
   * ``result`` snapshot, and nudged live on ``token_update``.
   */
  modelTokenUsage: ModelTokenUsage;
  /** The CLI's own context-window measurement for the last turn (v36).
   *  Drives the right panel's Context tab; null until a turn produces one. */
  contextBreakdown: ContextBreakdown | null;
  /**
   * Cumulative spend (USD) for the active conversation, shown in the chat
   * footer beside the token counter. ``totalCostUsd`` is the model
   * (Claude / OpenRouter) cost, ``totalPplxCostUsd`` the Perplexity tool
   * cost. Hydrated from the conversation row on open and *set* (not
   * incremented) from the absolute ``cost_totals`` snapshot on each
   * ``result`` — the source of truth is the server-accumulated column.
   */
  totalCostUsd: number;
  totalPplxCostUsd: number;

  /** Connect the multiplexed WS (call once after auth). */
  connectWs: () => void;
  loadConversations: () => Promise<void>;
  /** Fetch the admin "Others" rows for ``owner`` ("" = all authorized users)
   *  and remember the filter. Live upserts patch the result from then on. */
  loadTeamConversations: (owner: string) => Promise<void>;
  createConversation: (title?: string) => Promise<string>;
  /**
   * Reset the per-conversation *view* to the "fresh chat" baseline: model back
   * to Auto, no resolved effective model, and zeroed context (which releases
   * the model-switch lock, since the lock keys off ``contextTokens``).
   *
   * Models are per-conversation, so a fresh chat starts on Auto rather than
   * inheriting the previous chat's model. The pick the user makes in the
   * new-chat composer rides along on the first message and is persisted to the
   * new conversation's ``selected_model`` server-side. Called when the user
   * expresses new-conversation intent: the Home composer mounts (before any
   * chat is created) and again inside ``createConversation`` for the non-Home
   * entry points.
   */
  resetModelForNewConversation: () => void;
  openConversation: (id: string, opts?: { anchorMessageId?: string }) => Promise<void>;
  /**
   * Clear the per-conversation context/usage state (token counts, context
   * bar, cumulative totals) without touching the message list. Called when
   * the user lands on the home / new-conversation surface, where no
   * conversation is active: it prevents stale numbers from a previously
   * viewed chat — most visibly the model-switch lock, which keys off
   * ``contextTokens`` — from bleeding onto a brand-new chat. ``openConversation``
   * re-hydrates all of these from the server when an existing chat is reopened.
   */
  resetActiveContext: () => void;
  /** Fetch and prepend the next older page of messages. No-op if already
   *  loading, already at the start, or no conversation is active. */
  loadOlderMessages: () => Promise<void>;
  /** Fetch and append the next newer page of messages. No-op unless an
   *  anchored open left newer history unloaded below the viewport. */
  loadNewerMessages: () => Promise<void>;
  /** Clear ``pendingScrollTargetId`` after ``MessageList`` has consumed it to
   *  build its open-scroll target — so a later normal reopen doesn't re-jump. */
  consumeScrollTarget: () => void;
  /** Clear ``highlightedMessageId`` once the jump-to-message flash has played. */
  clearHighlightedMessage: () => void;
  /** Drop older messages once the window grows past
   *  ``MESSAGE_WINDOW_MAX`` and the user is anchored to the bottom. Safe
   *  to call freely — it short-circuits when the preconditions aren't met. */
  pruneOldMessagesIfSafe: () => void;
  setAtBottom: (atBottom: boolean) => void;
  /** Toggle the "reached the top of this chat" marker. No-ops when the
   *  value is unchanged so a stream of scroll events can't churn renders. */
  setHistoryTopReached: (reached: boolean) => void;
  toggleThinking: () => void;
  /**
   * The model chosen for the *active* conversation: the sentinel ``"auto"``
   * or a concrete model id. Per-conversation, not global — hydrated from the
   * conversation's ``selected_model`` on open, reset to ``"auto"`` for a fresh
   * chat. ``setSelectedModel`` persists changes to the open conversation
   * (pass ``persist=false`` from the new-chat composer, which has no
   * conversation to persist to — see the action for why).
   */
  selectedModel: string;
  setSelectedModel: (model: string, persist?: boolean) => void;
  /**
   * External MCP connectors (mcp_servers.id) opted into for the *active*
   * conversation. Per-conversation, not global — hydrated from the
   * conversation's ``enabled_connectors`` on open, reset to ``[]`` for a fresh
   * chat. Internal MCPs (cowork tools + Nbyula gateway) are always on and never
   * appear here. ``setEnabledConnectors`` persists to the open conversation
   * (which clears its frozen prefix server-side so the next turn re-freezes
   * with the new tool set). Mirrors the ``selectedModel`` pattern.
   */
  enabledConnectors: string[];
  setEnabledConnectors: (serverIds: string[], persist?: boolean) => void;
  /**
   * The model the backend actually resolved to for the most recent turn.
   * Differs from ``selectedModel`` when:
   *  - The user picked Auto and the resolver chose a specific provider.
   *  - The running agent called ``switch_model`` mid-conversation.
   * ``null`` until a turn completes (initial render before any send).
   */
  effectiveModel: string | null;
  /**
   * Recently-cooled-down providers, keyed by provider name. Updated by
   * the ``provider_cooled_down`` WS event so the model selector can
   * show a warning pill ("Claude rate-limited — using GPT-5.5"). Stale
   * entries are pruned on the next provider-status poll.
   */
  cooledDownProviders: Record<string, { kind: string; reason: string; ts: number }>;
  sendMessage: (content: string) => void;
  /** Internal: the actual send, after attachment re-homing. */
  _sendMessageNow: (content: string) => void;
  /**
   * Submit (or change) the current user's RLHF vote on an assistant message.
   * Optimistically flips the bubble's thumb, then POSTs to the feedback
   * endpoint — which records the vote locally and mirrors it to Langfuse as a
   * ``user-feedback`` score plus a replay dataset item. Rolls back and toasts
   * on failure. ``comment`` is the optional "what went wrong" note.
   */
  submitFeedback: (
    messageId: string,
    rating: "like" | "dislike",
    comment?: string,
  ) => Promise<void>;
  approveAction: (action: string, scope?: "once" | "session" | "always") => void;
  denyAction: () => void;
  answerQuestions: (answers: Record<string, string>) => void;
  dismissQuestions: () => void;
  interrupt: () => void;
  clearQueuedMessage: (convId?: string) => void;
  sendGenuiAction: (payload: unknown) => void;
  /** Pin an inline genui widget's HTML file into the Files panel as an
   *  artifact — the user-driven equivalent of the agent calling
   *  ``add_artifact``. Resolves to the saved artifact, or ``null`` on
   *  failure (a toast is surfaced). Idempotent: re-saving an already-saved
   *  widget returns the existing artifact without duplicating it. */
  saveWidgetAsArtifact: (
    genuiId: string,
  ) => Promise<{ id: string; filename: string; file_type: string; file_path?: string; created_at: string } | null>;
  handleWsMessage: (msg: WsServerMessage) => void;
  updateConversation: (id: string, updates: Partial<Conversation>) => Promise<void>;
  togglePinConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  /** Stamp ``last_read_at`` to now on the server and zero the local
   *  unread badge. Defaults to the active conversation. */
  markConversationRead: (id?: string) => Promise<void>;
  addFiles: (files: AttachedFile[]) => void;
  removeFile: (index: number) => void;
  clearFiles: () => void;
}

// Transient streaming + thinking state, cleared at every turn boundary
// (send / result / error / failover / conversation switch).
const STREAM_RESET = {
  streamingText: "",
  streamingBlock: null,
  thinkingText: "",
  thinkingDone: false,
  thinkingStartedAt: null,
  thinkingExpanded: true,
} as const;

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: [],
  teamConversations: [],
  teamOwnerFilter: "",
  subagentRuns: [],
  activeConversationId: null,
  executionElsewhere: null,
  unreadCutoff: null,
  messages: [],
  toolCalls: new Map(),
  subagents: new Map(),
  wsStatus: "disconnected",
  isAgentRunning: false,
  viewingAsManager: null,
  turnStartedAt: {},
  ...STREAM_RESET,
  lastStreamedMessageId: null,
  runningTasks: new Set(),
  pendingApproval: null,
  pendingQuestions: null,
  pendingQuestionRequestId: null,
  lastSendAt: {},
  preSendTurnStart: {},
  syncToastId: null,
  attachedFiles: [],
  homeDraft: "",
  pendingHomeSeed: null,
  evaluatorState: null,
  // Backend owns the queue; this mirror starts empty and is populated from
  // live send + ``message_queued`` / ``task_status.queued`` events.
  queuedMessages: {},
  artifacts: [],
  genuiHtml: new Map(),
  todos: [],
  rightPanelOpen: false,
  rightPanelTab: "files",
  openRightPanel: (tab) => set((s) => {
    if (tab) return { rightPanelOpen: true, rightPanelTab: tab };
    return { rightPanelOpen: !s.rightPanelOpen };
  }),
  closeRightPanel: () => set({ rightPanelOpen: false }),
  setRightPanelTab: (tab) => set({ rightPanelTab: tab }),
  _historyLoaded: false,
  _scrollToBottomTick: 0,
  hasMoreOlder: false,
  oldestLoaded: null,
  loadingOlder: false,
  hasMoreNewer: false,
  newestLoaded: null,
  loadingNewer: false,
  pendingScrollTargetId: null,
  highlightedMessageId: null,
  atBottom: true,
  historyTopReached: false,
  contextTokens: 0,
  // No conversation is open at construction, so the baseline limit is the
  // Auto ceiling. ``openConversation`` re-derives it from the conversation's
  // own ``selected_model``; the model is per-conversation, never global.
  contextLimit: getContextLimit("auto"),
  contextPercent: 0,
  contextModel: null,
  totalInputTokens: 0,
  totalOutputTokens: 0,
  totalCacheReadTokens: 0,
  totalCacheCreationTokens: 0,
  modelTokenUsage: {},
  contextBreakdown: null,
  totalCostUsd: 0,
  totalPplxCostUsd: 0,
  // The model for the *active* conversation. Hydrated from the conversation's
  // ``selected_model`` on open and reset to Auto for a fresh chat. There is no
  // global model preference — each conversation owns its model.
  selectedModel: "auto",
  effectiveModel: null,
  cooledDownProviders: {},
  // External connectors opted into for the active conversation. Empty = none
  // (default for a fresh chat). Hydrated from ``enabled_connectors`` on open.
  enabledConnectors: [],

  setEnabledConnectors: (serverIds: string[], persist = true) => {
    set({ enabledConnectors: serverIds });
    // Same per-conversation persistence contract as setSelectedModel: persist
    // to the open conversation only, and never from the new-chat composer
    // (``persist=false``) which isn't bound to ``activeConversationId``. The
    // PATCH clears the conversation's frozen prefix server-side so the next
    // turn re-freezes with the new tool set.
    if (!persist) return;
    const convId = get().activeConversationId;
    if (convId) {
      apiFetch(`/conversations/${convId}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled_connectors: serverIds }),
      }).catch(() => {});
    }
  },

  setSelectedModel: (model: string, persist = true) => {
    // Recompute the nudge against the new model's ceiling so the bar
    // re-scales immediately when the user switches mid-conversation.
    const prevModel = get().selectedModel;
    const tokens = get().contextTokens;
    const limit = getContextLimit(model);
    const percent = computeContextPercent(tokens, model);
    set({ selectedModel: model, contextLimit: limit, contextPercent: percent });
    // The model belongs to the conversation, so persist the pick server-side
    // when one is open. This makes a switch durable even if the user never
    // sends a message before leaving — without it, an unsent switch would be
    // lost on the next conversation open (which hydrates from the server).
    //
    // ``persist`` MUST be false when the caller is the new-chat composer
    // (HomeChatInput). That surface shares this store action but is NOT bound
    // to ``activeConversationId`` — which still points at the previously
    // opened chat, because the home surface never clears it. Persisting there
    // would PATCH the *wrong* conversation, silently overwriting the model of
    // the chat the user just left. The composer's pick instead rides along on
    // the create request (``createConversation`` → ``selected_model``) and is
    // re-persisted by the backend on the first turn.
    if (!persist) return;
    const convId = get().activeConversationId;
    if (convId) {
      // Drop an inline marker for a manual switch so the change is visible at
      // this point in the transcript, attributed to the person who made it.
      // Only a genuine change *to a concrete model* counts: switching back to
      // Auto isn't a "switched to <model>" event (the next turn's Auto
      // resolution announces the concrete model it lands on, by Auto Router).
      // The PATCH below persists the durable copy; this optimistic one renders
      // instantly and is replaced by the persisted row on the next reload.
      if (model !== "auto" && model !== prevModel) {
        const by = useAuthStore.getState().user?.name || "You";
        const switchMsg = buildModelSwitchMessage({ model, source: "manual", by });
        set((s) => ({ messages: [...s.messages, switchMsg] }));
      }
      apiFetch(`/conversations/${convId}`, {
        method: "PATCH",
        body: JSON.stringify({ selected_model: model }),
      }).catch(() => {
        // Non-fatal: the next turn re-persists ``selected_model`` anyway.
      });
    }
  },

  connectWs: () => {
    ws.connect(get().handleWsMessage, (status) => {
      set({ wsStatus: status });
      // Backstop for the real-time ``cloud_reauth_required`` event: if this socket
      // (re)connects AFTER the one-shot fired, re-read the durable flag so a dead
      // cloud credential still forces the re-login. Native-only in effect — the web
      // app's /sync/status is {role:"cloud"} with no ``reauth_required``.
      if (status === "connected") {
        apiFetch<{ reauth_required?: boolean }>("/sync/status")
          .then((s) => { if (s.reauth_required) void useAuthStore.getState().logout(); })
          .catch(() => {});
      }
    });
  },

  loadConversations: async () => {
    const res = await apiFetch<{ conversations: Conversation[] }>("/conversations");
    // Deduplicate by ID (defensive — server shouldn't return dupes, but concurrent
    // createConversation + loadConversations can cause local duplication)
    const seen = new Set<string>();
    const deduped = res.conversations.filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
    set({ conversations: deduped });
  },

  loadTeamConversations: async (owner: string) => {
    // Record the filter FIRST so an upsert racing this fetch is routed against
    // the filter the rows are about to reflect, not the previous one.
    set({ teamOwnerFilter: owner });
    try {
      const res = await fetchReportsConversations(owner || undefined);
      // A slower earlier request must not overwrite a newer filter's rows.
      if (get().teamOwnerFilter !== owner) return;
      set({ teamConversations: res.conversations });
    } catch {
      if (get().teamOwnerFilter === owner) set({ teamConversations: [] });
    }
  },

  resetModelForNewConversation: () => {
    // Models are per-conversation, so a fresh chat starts on Auto — it does
    // NOT inherit the model of whatever chat the user was just in. (The old
    // behaviour kept a single global ``cowork_model`` pick that leaked across
    // conversations; that concept is gone.) The user's pick in the new-chat
    // composer rides along on the first ``user_message`` and is persisted to
    // the new conversation's ``selected_model`` by the backend turn handler.
    //
    // We also reset the per-conversation *view*: the resolved effective model
    // and the context counters. Zeroing ``contextTokens`` is what releases the
    // model-switch lock (the lock keys off the token count, not the model), so
    // a fresh chat always opens unlocked regardless of how large the previous
    // chat's cached history was.
    set({
      selectedModel: "auto",
      effectiveModel: null,
      // A fresh chat starts with no external connectors (opt-in); the home
      // composer's picks ride along on createConversation, like the model.
      enabledConnectors: [],
      contextTokens: 0,
      contextLimit: getContextLimit("auto"),
      contextPercent: 0,
      contextModel: null,
    });
  },

  createConversation: async (title = "New Conversation") => {
    // Seed the new conversation with the current pick so it is the model's
    // owner from creation. The Home composer resets this to Auto on mount and
    // lets the user choose before submitting; non-Home entry points inherit
    // whatever model is active in their context. ``openConversation`` then hydrates the picker straight back from
    // ``selected_model``, so the pick the user made before the chat existed
    // survives the create → open → send round-trip.
    const selected_model = get().selectedModel;
    // Carry the home composer's connector opt-ins onto the new conversation
    // (same create→open round-trip as selected_model). Empty = none.
    const enabled_connectors = get().enabledConnectors;
    const conv = await apiFetch<Conversation>("/conversations", {
      method: "POST",
      body: JSON.stringify({
        title, selected_model, enabled_connectors,
      }),
    });
    // No active-view reset here: navigated entry points (Home)
    // immediately call ``openConversation``, which fully resets and re-hydrates
    // the context counters and switch lock for the new chat. Any future entry
    // point that creates a chat without navigating must leave the currently
    // viewed conversation's view untouched, so zeroing it here would be wrong.
    set((s) => ({
      conversations: [conv, ...s.conversations.filter((c) => c.id !== conv.id)],
    }));
    return conv.id;
  },

  resetActiveContext: () => {
    // Mirror the context/usage fields that ``openConversation`` resets, but
    // leave the message list and other UI state alone — this is for the home
    // surface, not a conversation switch. ``contextLimit`` falls back to the
    // selected model's ceiling so the context bar reads a sane baseline.
    set((s) => ({
      contextTokens: 0,
      contextLimit: getContextLimit(s.selectedModel),
      contextPercent: 0,
      contextModel: null,
      totalInputTokens: 0,
      totalOutputTokens: 0,
      totalCacheReadTokens: 0,
      totalCacheCreationTokens: 0,
      modelTokenUsage: {},
      contextBreakdown: null,
      totalCostUsd: 0,
      totalPplxCostUsd: 0,
    }));
  },

  openConversation: async (id: string, opts?: { anchorMessageId?: string }) => {
    // A search "jump to match": open the chat *at* this message rather than at
    // the bottom. Stashed in the store so ``MessageList`` (which remounts per
    // conversation) reads it at mount, and so it survives AppShell's URL→store
    // re-sync (which calls openConversation without opts).
    const anchorMessageId = opts?.anchorMessageId ?? null;
    // Reset UI state for the new conversation.  isAgentRunning will be
    // corrected by the server's ``task_status`` message after subscribe.
    set({
      activeConversationId: id,
      // Reset the cutoff until the detail fetch lands — otherwise a
      // stale value from the previous chat would briefly render its
      // divider position over the new conversation.
      unreadCutoff: null,
      messages: [],
      toolCalls: new Map(),
      subagents: new Map(),
      // Cleared for the same reason as ``unreadCutoff``: the durable runs are
      // re-fetched below, and until that lands the PREVIOUS chat's rows are
      // still in the store. Harmless while only the right panel read them —
      // it is usually closed — but the composer's running-tasks chip is always
      // visible, so a stale count would announce work on the wrong chat.
      subagentRuns: [],
      isAgentRunning: false,
      viewingAsManager: null,
      ...STREAM_RESET,
      lastStreamedMessageId: null,
      pendingApproval: null,
      pendingQuestions: null,
      pendingQuestionRequestId: null,
      evaluatorState: null,
      artifacts: [],
      genuiHtml: new Map(),
      // Clear the previous conversation's plan before we hydrate this
      // one's — the Plan tab must never show stale todos from another
      // chat.
      todos: [],
      rightPanelOpen: false,
      _historyLoaded: false,
      hasMoreOlder: false,
      oldestLoaded: null,
      loadingOlder: false,
      hasMoreNewer: false,
      newestLoaded: null,
      loadingNewer: false,
      // Carry the jump target through the reset so MessageList sees it at
      // mount; cleared after consumption (or null for an ordinary open).
      pendingScrollTargetId: anchorMessageId,
      highlightedMessageId: anchorMessageId,
      // An anchored open lands mid-conversation, not at the bottom.
      atBottom: anchorMessageId === null,
      // A freshly opened chat opens at the bottom, so the top marker must
      // start hidden — the scroll handler turns it on only if the user
      // scrolls up to the exhausted top of a scrollable conversation.
      historyTopReached: false,
      // Reset the compact nudge so a dismissal on conversation A does
      // not carry over to conversation B. Context numbers get
      // re-hydrated a few lines below.
      contextTokens: 0,
      contextPercent: 0,
      contextModel: null,
      // Drop the previous chat's resolved-model annotation now; the hydrate
      // below restores this conversation's own. ``selectedModel`` is left as
      // is until hydration so a non-Auto chat doesn't flash "Auto" first.
      effectiveModel: null,
      // Clear the prior chat's connector opt-ins; hydration restores this
      // conversation's own a few lines below.
      enabledConnectors: [],
      totalInputTokens: 0,
      totalOutputTokens: 0,
      totalCacheReadTokens: 0,
      totalCacheCreationTokens: 0,
      modelTokenUsage: {},
      contextBreakdown: null,
      totalCostUsd: 0,
      totalPplxCostUsd: 0,
    });
    useNavStore.getState().navigate(`/chat/${id}`);

    // Floor the perceived load to ``MIN_HISTORY_LOAD_MS`` so the shimmer
    // doesn't strobe — on a same-machine dev setup the detail fetch
    // resolves in <50 ms, which makes the loading→content transition
    // feel jerky. Real-world remote latency naturally exceeds this and
    // ``Promise.all`` simply resolves at the slower of the two.
    let detail: ConversationDetail;
    try {
      const anchorQuery = anchorMessageId
        ? `?anchor=${encodeURIComponent(anchorMessageId)}`
        : "";
      const [fetched] = await Promise.all([
        apiFetch<ConversationDetail>(`/conversations/${id}${anchorQuery}`),
        new Promise<void>((r) => setTimeout(r, MIN_HISTORY_LOAD_MS)),
      ]);
      detail = fetched;
    } catch {
      useToastStore.getState().add("Couldn't load conversation — please try again.", "error");
      useNavStore.getState().navigate("/home");
      set({ activeConversationId: null });
      return;
    }

    // Load artifacts for this conversation
    const rawArtifacts = detail.artifacts || [];

    // Inject artifact cards into message list
    const artifactMsgs: Message[] = rawArtifacts.map(
      (a) => ({
        id: `artifact-${a.id}`,
        role: "artifact" as const,
        content_json: JSON.stringify([{
          type: "artifact",
          artifact_id: a.id,
          filename: a.filename,
          file_type: a.file_type,
        }]),
        created_at: a.created_at,
      }),
    );

    // Interleave artifacts at their correct timeline position (by created_at)
    // instead of appending them all at the end of the chat.
    const allMessages = [...detail.messages, ...artifactMsgs].sort(
      // parseUTC, NOT new Date(): SQLite rows carry bare "YYYY-MM-DD HH:MM:SS"
      // (UTC), which new Date() parses as LOCAL time — 5.5h off in IST — while
      // in-memory rows (optimistic bubbles, streamed events) carry T-format.
      // Mixing the two bases scrambled the transcript order after a synced
      // merge until reload (observed live).
      (a, b) => parseUTC(a.created_at).getTime() - parseUTC(b.created_at).getTime(),
    );
    // Rebuild genuiHtml map from historical genui messages
    const genuiMap = new Map<string, string>();
    for (const msg of allMessages) {
      if (msg.role === "genui") {
        try {
          const blocks = JSON.parse(msg.content_json);
          const block = blocks.find((b: { type: string }) => b.type === "genui");
          if (block?.genui_id && block?.html) {
            genuiMap.set(block.genui_id, block.html);
          }
        } catch { /* ignore */ }
      }
    }
    // Rehydrate the latest TodoWrite state so the panel survives
    // conversation switches. We walk newest-first and take the first
    // TodoWrite tool_use we see — that's always the agent's most
    // recent plan because TodoWrite emits the full list each time.
    let hydratedTodos: AgentTodo[] = [];
    for (let i = allMessages.length - 1; i >= 0; i--) {
      const msg = allMessages[i];
      if (msg && msg.role === "tool_use" && msg.tool_name === "TodoWrite") {
        try {
          const blocks = JSON.parse(msg.content_json);
          const useBlock = blocks.find((b: { type: string }) => b.type === "tool_use");
          const todos = useBlock?.input?.todos;
          if (Array.isArray(todos)) {
            hydratedTodos = todos as AgentTodo[];
            break;
          }
        } catch { /* ignore */ }
      }
    }
    // Hydrate the context-usage display from the conversation row so
    // the nudge can fire immediately on reload — without this we'd
    // only see the bar after the next ResultMessage lands.
    const persistedTokens = detail.context_tokens ?? 0;
    // The model is a property of *this* conversation. ``selected_model`` is
    // the user's choice (Auto or a concrete id) and drives the picker;
    // ``context_model`` is the last resolved model and drives the context
    // ceiling. Both come from the server — never from the previously viewed
    // chat's state — so opening a chat never inherits another chat's model.
    //
    // Pre-v20 chats predate ``selected_model`` and read as null. The migration
    // backfills it from ``context_model``; we repeat that fallback here so a DB
    // that only went through the column-heal path (no backfill) still reopens
    // an old chat on the model it last ran on rather than flipping it to Auto
    // — which would switch providers and re-bill the cached history next turn.
    const persistedSelected = detail.selected_model ?? detail.context_model ?? "auto";
    const persistedModel = detail.context_model ?? persistedSelected;
    // Freeze the cutoff at the value the server had *before* we mark
    // this conversation read. The mark-read POST below advances the
    // server-side timestamp; if we read it after, the divider would
    // never render. We only keep the cutoff when there's actually an
    // assistant message past it in the loaded history — otherwise the
    // divider would show up above the *first* live reply in a chat
    // the user is watching being generated, which isn't "unread".
    let cutoffAtOpen: string | null = detail.last_read_at ?? null;
    if (cutoffAtOpen) {
      const cutoffMs = parseUTC(cutoffAtOpen).getTime();
      const hasUnreadInHistory = allMessages.some(
        (m) => m.role !== "user" && m.role !== "tool_result" && m.role !== "model_switch"
          && parseUTC(m.created_at).getTime() > cutoffMs,
      );
      if (!hasUnreadInHistory) cutoffAtOpen = null;
    }
    // Restore the clarification widget if the agent is blocked on an
    // AskUserQuestion in this conversation. Without this, switching
    // away and back — or closing and re-opening the app — would lose
    // the widget even though the agent is still waiting on a reply.
    // openConversation() above cleared these to null on switch; this
    // re-populates them strictly from the server's source of truth.
    const pq = detail.pending_question ?? null;
    set({
      messages: allMessages,
      artifacts: rawArtifacts,
      genuiHtml: genuiMap,
      todos: hydratedTodos,
      unreadCutoff: cutoffAtOpen,
      _historyLoaded: true,
      // Pagination state: the server tells us whether more history
      // exists. ``oldest_message_at`` is the cursor we'll feed back to
      // ``/messages?before=`` when the user scrolls up. Artifact
      // pseudo-rows can have older ``created_at`` than the first real
      // message in some legacy chats — use the actual real-message
      // boundary the server gave us, not the synthesised one.
      hasMoreOlder: !!detail.has_more_messages,
      oldestLoaded: detail.oldest_message_at ?? null,
      loadingOlder: false,
      // Forward pagination: only ever active after an anchored open that left
      // newer messages unloaded below. A normal open holds the newest message,
      // so the server returns has_more_newer=false here.
      hasMoreNewer: !!detail.has_more_newer,
      newestLoaded: detail.newest_message_at ?? null,
      loadingNewer: false,
      // Stay off the bottom for an anchored open so auto-prune (bottom-only)
      // can't trim the newer messages we just loaded out from under the jump.
      atBottom: anchorMessageId === null,
      contextTokens: persistedTokens,
      contextLimit: getContextLimit(persistedModel),
      contextPercent: computeContextPercent(persistedTokens, persistedModel),
      contextModel: persistedModel,
      // Restore the picker to this conversation's own model, and seed the
      // last Auto-resolved model. Both are reset here (rather than leaking
      // from the prior chat) so the selector reflects the conversation the
      // user is actually viewing.
      selectedModel: persistedSelected,
      effectiveModel: detail.context_model ?? null,
      // Restore this conversation's external-connector opt-ins (empty when the
      // chat hasn't turned any on). Per-conversation, like the model.
      enabledConnectors: detail.enabled_connectors ?? [],
      totalInputTokens: detail.input_tokens ?? 0,
      totalOutputTokens: detail.output_tokens ?? 0,
      totalCacheReadTokens: detail.cache_read_tokens ?? 0,
      totalCacheCreationTokens: detail.cache_creation_tokens ?? 0,
      modelTokenUsage: detail.model_token_usage ?? {},
      contextBreakdown: detail.context_breakdown ?? null,
      totalCostUsd: detail.cost_usd ?? 0,
      totalPplxCostUsd: detail.pplx_cost_usd ?? 0,
      pendingQuestions: pq?.questions ?? null,
      pendingQuestionRequestId: pq?.request_id ?? null,
      // ADR-0009: the backend sets viewer_is_manager only when this chat
      // belongs to one of the viewer's reports — surface the banner.
      viewingAsManager: detail.viewer_is_manager
        ? {
            ownerName: detail.owner_name ?? detail.owner_email ?? "the owner",
            ownerEmail: detail.owner_email ?? "",
          }
        : null,
    });

    // Durable running state (#79): if the row says a turn is running (possibly
    // on ANOTHER environment), light the in-chat spinner and anchor the elapsed
    // timer to the TRUE start time. A locally-running turn re-asserts via
    // task_status on subscribe anyway; this only adds signal, never clears.
    if (detail.agent_running) {
      const startedMs = detail.active_turn_started_at
        ? parseUTC(detail.active_turn_started_at).getTime()
        : null;
      set((s) => ({
        isAgentRunning: true,
        // Prefer the EARLIEST anchor: the row carries the turn's true start,
        // which beats a "now" stamp from a reconnect replay — so the elapsed
        // timer survives reloads instead of restarting at 0s.
        turnStartedAt:
          startedMs != null && startedMs < (s.turnStartedAt[id] ?? Infinity)
            ? { ...s.turnStartedAt, [id]: startedMs }
            : s.turnStartedAt,
      }));
    }

    // Load associated shortlist if one exists
    const shortlistStore = useShortlistStore.getState();
    if (detail.shortlist) {
      shortlistStore.loadShortlist(detail.shortlist.id);
    } else {
      useShortlistStore.setState({ activeShortlist: null, liveData: null });
    }

    // NOTE: opening a conversation deliberately does NOT auto-open the right
    // panel any more. On mobile it took over the whole screen (hiding the
    // chat) and on desktop it was uninvited; the user reaches Plan/Shortlist/
    // Files via the edge strip (desktop) or the header button (mobile). Live
    // events (a plan or shortlist appearing mid-run) still auto-open their tab.

    // Subscribe to this conversation's live events via the multiplexed WS
    ws.subscribe(id);

    // Seed the Sub-agents panel from the durable table. The WS frame keeps it
    // fresh from here on, but the initial read has to come from the store: a
    // panel that only ever learns from events shows nothing for every run that
    // happened before this socket existed — which is most of them.
    void fetchSubagentRuns(id)
      .then((runs) => {
        // Guard against a slow fetch landing after the user moved on.
        if (get().activeConversationId === id) set({ subagentRuns: runs });
      })
      .catch(() => {
        if (get().activeConversationId === id) set({ subagentRuns: [] });
      });

    // Clear the unread badge on the server and locally. Fire-and-forget
    // — the user can read the conversation perfectly fine even if this
    // round-trip fails; we just won't sync the badge until next reload.
    void get().markConversationRead(id);
  },

  loadOlderMessages: async () => {
    const state = get();
    const convId = state.activeConversationId;
    if (!convId) return;
    if (!state.hasMoreOlder || state.loadingOlder || !state.oldestLoaded) return;

    set({ loadingOlder: true });
    try {
      const before = encodeURIComponent(state.oldestLoaded);
      const page = await apiFetch<MessagesPage>(
        `/conversations/${convId}/messages?before=${before}&limit=${MESSAGE_PAGE_SIZE}`,
      );
      // Guard against conversation switch mid-flight: if the user moved
      // to another chat while we were fetching, drop the response. The
      // newly-opened conversation has already reset paging state and
      // would otherwise be polluted with the wrong history.
      set((s) => {
        if (s.activeConversationId !== convId) return { loadingOlder: false };
        if (page.messages.length === 0) {
          return {
            loadingOlder: false,
            hasMoreOlder: false,
          };
        }
        // De-duplicate against current messages (a redundant fetch
        // shouldn't double-insert) and prepend in chronological order.
        const existingIds = new Set(s.messages.map((m) => m.id));
        const incoming = page.messages.filter((m) => !existingIds.has(m.id));
        const merged = [...incoming, ...s.messages].sort(
          // parseUTC, not new Date() — see the openConversation sort comment.
          (a, b) => parseUTC(a.created_at).getTime() - parseUTC(b.created_at).getTime(),
        );
        return {
          messages: merged,
          hasMoreOlder: page.has_more,
          oldestLoaded: page.oldest_loaded,
          loadingOlder: false,
        };
      });
    } catch {
      // Reset the flag so the user can retry on their next scroll.
      // Don't surface a toast — failures during paging are recoverable
      // and the UI will just stop showing the loading shimmer.
      set({ loadingOlder: false });
    }
  },

  loadNewerMessages: async () => {
    const state = get();
    const convId = state.activeConversationId;
    if (!convId) return;
    if (!state.hasMoreNewer || state.loadingNewer || !state.newestLoaded) return;

    set({ loadingNewer: true });
    try {
      const after = encodeURIComponent(state.newestLoaded);
      const page = await apiFetch<MessagesPage>(
        `/conversations/${convId}/messages?after=${after}&limit=${MESSAGE_PAGE_SIZE}`,
      );
      // Same conversation-switch guard as loadOlderMessages: drop a response
      // that arrives after the user moved on.
      set((s) => {
        if (s.activeConversationId !== convId) return { loadingNewer: false };
        if (page.messages.length === 0) {
          return { loadingNewer: false, hasMoreNewer: false };
        }
        // De-duplicate and append in chronological order (forward pages add
        // at the bottom — no firstItemIndex shift needed, unlike prepends).
        const existingIds = new Set(s.messages.map((m) => m.id));
        const incoming = page.messages.filter((m) => !existingIds.has(m.id));
        const merged = [...s.messages, ...incoming].sort(
          // parseUTC, not new Date() — see the openConversation sort comment.
          (a, b) => parseUTC(a.created_at).getTime() - parseUTC(b.created_at).getTime(),
        );
        return {
          messages: merged,
          hasMoreNewer: page.has_more,
          newestLoaded: page.newest_loaded ?? s.newestLoaded,
          loadingNewer: false,
        };
      });
    } catch {
      set({ loadingNewer: false });
    }
  },

  consumeScrollTarget: () => {
    if (get().pendingScrollTargetId === null) return;
    set({ pendingScrollTargetId: null });
  },

  clearHighlightedMessage: () => {
    if (get().highlightedMessageId === null) return;
    set({ highlightedMessageId: null });
  },

  setAtBottom: (atBottom) => {
    if (get().atBottom === atBottom) return;
    set({ atBottom });
    // When the user returns to the bottom, that's the cheapest moment
    // to trim the in-memory window.
    if (atBottom) get().pruneOldMessagesIfSafe();
  },

  setHistoryTopReached: (reached) => {
    if (get().historyTopReached === reached) return;
    set({ historyTopReached: reached });
  },

  toggleThinking: () => set((s) => ({ thinkingExpanded: !s.thinkingExpanded })),

  pruneOldMessagesIfSafe: () => {
    const state = get();
    // Hard gates: anchored at bottom, not streaming, window oversized.
    // Streaming is excluded because the live ``toolCalls`` map points
    // at message IDs in the current turn — pruning past them would
    // strand a running ToolCallGroup with no anchor row.
    if (!state.atBottom || state.isAgentRunning) return;
    if (state.messages.length <= MESSAGE_WINDOW_MAX) return;

    const cutIdx = state.messages.length - MESSAGE_WINDOW_PRUNE_TO;
    const kept = state.messages.slice(cutIdx);
    // The dropped slice may have contained artifact pseudo-rows that
    // the right panel / file list still reference via state.artifacts
    // — those survive untouched. The cards within the in-chat scroll
    // are the only thing lost, and they'd reappear if the user
    // re-opened the conversation.
    set({
      messages: kept,
      oldestLoaded: kept[0]?.created_at ?? null,
      hasMoreOlder: true,
    });
  },

  sendMessage: (content: string) => {
    const state = get();
    const convId = state.activeConversationId;
    if (!convId) return;

    // Re-home attachments staged where THIS conversation's agent can't reach
    // them. Two cases, both from the composer uploading at attach time before
    // the target chat exists:
    //   • under ANOTHER conversation's ``exports/<conv>/_uploads`` (home
    //     composer inherited the previously-active chat's id), or
    //   • in the shared ``.nbyula-cowork/drops`` dir (no conversation was
    //     active at all — the home-page default).
    // The cloud sandbox seeds ONLY this conversation's workspace, so either is
    // invisible to the agent ("uploaded doc can't be accessed"). claim moves
    // the files server-side and we rewrite the paths in the content before
    // sending. Fail-soft — on any error the original content goes out unchanged
    // (desktop shared-FS still works).
    //
    // Paths are appended one-per-line ("Attached file: <path>" / "- <path>"),
    // so we scan LINE-BY-LINE and take the path from its first "/" to the end of
    // the line. A prior ``[^\s]+`` regex truncated at the first space, so a
    // filename with a space ("CourseFinder (47).xlsx") produced a non-existent
    // path the claim endpoint couldn't move — the file then stayed foreign and
    // the agent reported "path doesn't resolve". End-of-line capture keeps the
    // full name (and the leading ``~/.nbyula-cowork`` prefix the server's
    // ``resolve_under_home`` requires).
    const foreignSet = new Set<string>();
    for (const line of content.split("\n")) {
      const hasUploads = /\/exports\/[0-9a-f]{32}\/_uploads\//.test(line);
      const hasDrops = line.includes(".nbyula-cowork/drops/");
      if (!hasUploads && !hasDrops) continue;
      // The "Attached file:" / "-" prefixes contain no "/", so the first slash
      // begins the absolute path; the rest of the line is the (possibly
      // space-bearing) filename.
      const slash = line.indexOf("/");
      if (slash < 0) continue;
      const path = line.slice(slash).trim();
      if (hasDrops || !path.includes(`/exports/${convId}/`)) foreignSet.add(path);
    }
    const foreign = [...foreignSet];
    if (foreign.length > 0) {
      void (async () => {
        let fixed = content;
        try {
          const res = await apiFetch<{ moved: Record<string, string> }>("/files/claim", {
            method: "POST",
            body: JSON.stringify({ paths: foreign, conversation_id: convId }),
          });
          for (const [oldPath, newPath] of Object.entries(res.moved ?? {})) {
            fixed = fixed.split(oldPath).join(newPath);
          }
        } catch {
          /* claim is best-effort */
        }
        get()._sendMessageNow(fixed);
      })();
      return;
    }
    get()._sendMessageNow(content);
  },

  _sendMessageNow: (content: string) => {
    const state = get();
    const convId = state.activeConversationId;
    if (!convId) return;

    // A client-side id ties this send to its server-side queue entry, so the
    // ``message_dequeued`` echo can promote the exact optimistic bubble when
    // the message starts running.
    const clientMsgId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Always send immediately — the backend owns the queue. If a run is in
    // flight the server enqueues this message and drains it at the next turn
    // boundary (acking with ``message_queued``); it is never rejected or held
    // in the browser waiting for a ``task_status`` flush (the old race that
    // dropped messages on Stop). Optimistically render it as a distinct
    // "queued" bubble; ``message_dequeued`` converts it to a real one.
    if (state.isAgentRunning) {
      set((s) => {
        const queuedMessages = {
          ...s.queuedMessages,
          [convId]: [...(s.queuedMessages[convId] ?? []), { id: clientMsgId, text: content }],
        };
        // Snap to the bottom only when the queued send is for the visible
        // conversation, so the user can watch the in-flight response.
        return s.activeConversationId === convId
          ? { queuedMessages, _scrollToBottomTick: s._scrollToBottomTick + 1 }
          : { queuedMessages };
      });
      ws.send({ type: "user_message", conversation_id: convId, content, model: state.selectedModel, client_msg_id: clientMsgId });
      return;
    }

    const userMsg: Message = {
      id: `temp-${Date.now()}`,
      role: "user",
      content_json: JSON.stringify([{ type: "text", text: content }]),
      created_at: new Date().toISOString(),
    };
    // Reset toolCalls per message — each agent run is independent.
    // Guard against active-conversation drift between the capture above
    // and this set: if the user switched conversations between get()
    // and now (e.g. via an async path that ever interleaves), we still
    // send the WS message to the captured convId, but the UI mutation
    // only fires when the bubble belongs to the visible conversation.
    set((s) => {
      if (s.activeConversationId !== convId) return s;
      return {
        messages: [...s.messages, userMsg],
        isAgentRunning: true,
        ...STREAM_RESET,
        // Stamp the send so a stale `task_status{running:false}` from the
        // pre-send `subscribe` (new chat: no session yet) can't clobber the
        // just-started run and blank the indicator until the worker streams.
        // The indicator is a constant "Working" now (no cold-start line), so
        // there's nothing else to set optimistically here.
        lastSendAt: { ...s.lastSendAt, [convId]: Date.now() },
        // ...and remember which turn-start the row carried BEFORE this send.
        // A not-running row bumped between the send and `begin_turn`
        // (mark-read, lease-owner stamp) still shows this same old start, and
        // must not count as "our turn finished" (2026-08-04: indicator showed
        // 2s, blanked, reply landed later — every follow-up turn).
        preSendTurnStart: {
          ...s.preSendTurnStart,
          [convId]: (() => {
            const row =
              s.conversations.find((c) => c.id === convId) ??
              s.teamConversations.find((c) => c.id === convId);
            const at = (row as { active_turn_started_at?: string } | undefined)
              ?.active_turn_started_at;
            return at ? parseUTC(at).getTime() : 0;
          })(),
        },
        toolCalls: new Map(),
        subagents: new Map(),
        // Bumping the tick signals MessageList to jump to the bottom
        // regardless of where the user was scrolled — sending a
        // message implies they want to watch the response.
        _scrollToBottomTick: s._scrollToBottomTick + 1,
      };
    });
    ws.send({ type: "user_message", conversation_id: convId, content, model: state.selectedModel, client_msg_id: clientMsgId });

    // Auto-title on first user message (only if history has loaded to avoid race).
    //
    // Also guarded against ``hasMoreOlder`` — once the window has been pruned
    // mid-conversation the in-memory ``messages`` array no longer carries the
    // historical user messages, and a naïve count would mis-classify the
    // current message as the first ever and overwrite an already-set title.
    const isFirstMessage =
      state._historyLoaded
      && !state.hasMoreOlder
      && state.messages.filter((m) => m.role === "user").length === 0;
    if (isFirstMessage) {
      const title = content.length > 60 ? content.slice(0, 57).trim() + "..." : content;
      apiFetch(`/conversations/${convId}`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      }).then(() => {
        set((s) => ({
          conversations: s.conversations.map((c) => (c.id === convId ? { ...c, title } : c)),
        }));
      }).catch(() => {});
    }
  },

  submitFeedback: async (messageId, rating, comment) => {
    const convId = get().activeConversationId;
    if (!convId) return;
    // Snapshot the prior vote so we can roll back if the request fails.
    const prev = get().messages.find((m) => m.id === messageId);
    const prevRating = prev?.feedback ?? null;
    const prevComment = prev?.feedback_comment ?? null;
    // Optimistically flip the thumb so the UI feels instant.
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === messageId ? { ...m, feedback: rating, feedback_comment: comment ?? null } : m,
      ),
    }));
    try {
      await apiFetch(`/conversations/${convId}/messages/${messageId}/feedback`, {
        method: "POST",
        body: JSON.stringify({ rating, comment: comment ?? null }),
      });
    } catch (e) {
      // Roll back to the prior vote and surface the failure — the local DB
      // write didn't land, so the optimistic state would otherwise be a lie.
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === messageId ? { ...m, feedback: prevRating, feedback_comment: prevComment } : m,
        ),
      }));
      useToastStore
        .getState()
        .add(e instanceof Error ? e.message : "Couldn't save your feedback.", "error");
    }
  },

  approveAction: (action: string, scope: "once" | "session" | "always" = "once") => {
    const convId = get().activeConversationId;
    if (!convId) return;
    const request_id = get().pendingApproval?.request_id;
    ws.send({ type: "approve_action", conversation_id: convId, action, scope, request_id });
    set({ pendingApproval: null, isAgentRunning: true });
  },

  denyAction: () => {
    const convId = get().activeConversationId;
    if (!convId) return;
    const request_id = get().pendingApproval?.request_id;
    ws.send({ type: "deny_action", conversation_id: convId, request_id });
    set({ pendingApproval: null, isAgentRunning: true });
  },

  answerQuestions: (answers: Record<string, string>) => {
    const convId = get().activeConversationId;
    if (!convId) return;
    const request_id = get().pendingQuestionRequestId ?? undefined;
    ws.send({ type: "user_question_answer", conversation_id: convId, answers, request_id });
    // Resolve any running AskUserQuestion tool calls
    set((s) => {
      const calls = new Map(s.toolCalls);
      for (const [id, tc] of calls) {
        if (tc.tool_name === "AskUserQuestion" && tc.status === "running") {
          calls.set(id, { ...tc, status: "success", result: "Answered", duration_ms: Date.now() - tc.started_at });
        }
      }
      return { pendingQuestions: null, pendingQuestionRequestId: null, isAgentRunning: true, toolCalls: calls };
    });
  },

  dismissQuestions: () => {
    const convId = get().activeConversationId;
    if (!convId) return;
    const request_id = get().pendingQuestionRequestId ?? undefined;
    ws.send({ type: "deny_action", conversation_id: convId, request_id });
    // Resolve any running AskUserQuestion tool calls as skipped
    set((s) => {
      const calls = new Map(s.toolCalls);
      for (const [id, tc] of calls) {
        if (tc.tool_name === "AskUserQuestion" && tc.status === "running") {
          calls.set(id, { ...tc, status: "success", result: "Skipped", duration_ms: Date.now() - tc.started_at });
        }
      }
      return { pendingQuestions: null, pendingQuestionRequestId: null, isAgentRunning: true, toolCalls: calls };
    });
  },

  sendGenuiAction: (payload: unknown) => {
    const convId = get().activeConversationId;
    if (!convId) return;
    ws.send({ type: "genui_action", conversation_id: convId, payload });
  },

  saveWidgetAsArtifact: async (genuiId: string) => {
    try {
      const artifact = await apiFetch<{
        id: string;
        filename: string;
        file_type: string;
        file_path?: string;
        created_at: string;
      }>("/shortlists/artifacts/from-widget", {
        method: "POST",
        body: JSON.stringify({ genui_id: genuiId }),
      });
      // Merge into the artifacts list (dedupe by id). On success the widget
      // de-dups out of the Files panel's Widgets section by basename, so it
      // surfaces under Artifacts instead — no duplicate card.
      set((s) =>
        s.artifacts.some((a) => a.id === artifact.id)
          ? s
          : { artifacts: [...s.artifacts, artifact] },
      );
      useToastStore.getState().add("Saved to Files", "success");
      return artifact;
    } catch (e) {
      useToastStore
        .getState()
        .add(e instanceof Error ? e.message : "Couldn't save the widget to Files.", "error");
      return null;
    }
  },

  clearQueuedMessage: (convId?: string) => {
    const id = convId || get().activeConversationId;
    if (!id) return;
    // Drop the backlog on the server (the live run is untouched); clear the
    // local mirror optimistically. The ``queue_cleared`` echo reconciles.
    ws.send({ type: "clear_queue", conversation_id: id });
    set((s) => {
      const updated = { ...s.queuedMessages };
      delete updated[id];
      return { queuedMessages: updated };
    });
  },

  interrupt: () => {
    const convId = get().activeConversationId;
    if (!convId) return;
    ws.send({ type: "interrupt", conversation_id: convId });
    // Mark any still-running tool calls as error so spinners stop
    set((s) => {
      const calls = new Map(s.toolCalls);
      for (const [id, tc] of calls) {
        if (tc.status === "running") {
          calls.set(id, { ...tc, status: "error", result: "Interrupted by user", is_error: true, duration_ms: Date.now() - tc.started_at });
        }
      }
      return { isAgentRunning: false, toolCalls: calls };
    });
  },

  handleWsMessage: (msg: WsServerMessage) => {
    const activeId = get().activeConversationId;

    // Debug trace for the lossy-event class of bugs (stuck spinner / missing
    // reply): logs every state-bearing frame with the fields the handlers key
    // on, so DevTools shows exactly what arrived vs. what the UI did with it.
    // Filter with "[cowork-ws]".
    if (
      msg.type === "task_status" ||
      msg.type === "conversation_upsert" ||
      msg.type === "conversation_synced" ||
      msg.type === "job_complete"
    ) {
      const conv = (msg as { conversation?: { agent_running?: unknown; updated_at?: string } }).conversation;
      console.debug(
        "[cowork-ws]", msg.type,
        (msg as { conversation_id?: string }).conversation_id ?? conv,
        msg.type === "task_status" ? `running=${(msg as { running?: boolean }).running}` : "",
        conv ? `agent_running=${conv.agent_running} updated_at=${conv.updated_at}` : "",
        `active=${activeId?.slice(0, 8)} isAgentRunning=${get().isAgentRunning}`,
      );
    }

    // The device's cloud credential is unrecoverable (no cloud token + no Google
    // refresh token to heal from). Hard-logout so the user re-signs-in and
    // re-captures it locally — a soft logout would silently restore the same
    // broken state from the server-side session. Backend only fires this on a
    // DB-only check, never on a transient cloud/Google outage.
    if (msg.type === "cloud_reauth_required") {
      void useAuthStore.getState().logout();
      return;
    }

    // task_status is always processed (even for non-active conversations)
    if (msg.type === "task_status") {
      set((s) => {
        const next = new Set(s.runningTasks);
        if (msg.running) {
          next.add(msg.conversation_id);
        } else {
          next.delete(msg.conversation_id);
        }
        // When a task stops for the active conversation, mark any lingering running tool calls as error
        const isActive = msg.conversation_id === activeId;
        let toolCalls = s.toolCalls;
        if (isActive && !msg.running) {
          toolCalls = new Map(s.toolCalls);
          for (const [id, tc] of toolCalls) {
            if (tc.status === "running") {
              toolCalls.set(id, { ...tc, status: "error", result: "Task stopped", is_error: true, duration_ms: Date.now() - tc.started_at });
            }
          }
        }
        // Re-hydrate the queued-bubble mirror from the server's authoritative
        // list (sent on subscribe/reconnect). The backend owns the queue, so
        // this recovers pending sends after a reload without any localStorage
        // flush race. Synthesise ids for entries we didn't originate — a later
        // ``message_dequeued`` falls back to content matching for those.
        let queuedMessages = s.queuedMessages;
        if (msg.queued !== undefined) {
          const mirror = msg.queued.map((text, i) => ({ id: `srv-${msg.conversation_id}-${i}`, text }));
          if (mirror.length) {
            queuedMessages = { ...s.queuedMessages, [msg.conversation_id]: mirror };
          } else if (s.queuedMessages[msg.conversation_id]) {
            queuedMessages = { ...s.queuedMessages };
            delete queuedMessages[msg.conversation_id];
          }
        }
        // Ignore a stale `running:false` that lands within a short window of a
        // local send — e.g. the pre-send `subscribe` on a new chat reports "no
        // session yet", which would otherwise blank the just-started run's
        // indicator until the worker streams (~cold-start seconds later).
        const staleStop =
          !msg.running && isActive &&
          Date.now() - (s.lastSendAt[msg.conversation_id] || 0) < 8000;
        return {
          runningTasks: next,
          toolCalls,
          queuedMessages,
          ...(isActive && !staleStop ? { isAgentRunning: msg.running } : {}),
        };
      });
      // NOTE: no client-side queue flush here any more. The server drains its
      // own per-conversation queue at the next turn boundary and echoes
      // ``message_dequeued`` when each queued message starts running. This
      // removes the old 300ms ``setTimeout`` that raced the backend's
      // "still processing" guard and could silently drop the message on Stop.
      return;
    }

    // A message was accepted into the server-side queue while the agent was
    // busy. The optimistic bubble was already added on send; reconcile it in
    // case this client didn't originate the send (e.g. a second window).
    if (msg.type === "message_queued") {
      set((s) => {
        const existing = s.queuedMessages[msg.conversation_id] ?? [];
        const known = msg.client_msg_id && existing.some((m) => m.id === msg.client_msg_id);
        if (known) return s;
        const id = msg.client_msg_id ?? `srv-${msg.conversation_id}-${existing.length}`;
        return {
          queuedMessages: {
            ...s.queuedMessages,
            [msg.conversation_id]: [...existing, { id, text: msg.content }],
          },
        };
      });
      return;
    }

    // A queued message has started running as the next turn. Promote its
    // optimistic "queued" bubble to a real user bubble — or, on a window that
    // did NOT originate the send (real-time multi-client, ADR-0008), create the
    // bubble so the message another client sent shows up here live.
    if (msg.type === "message_dequeued") {
      set((s) => {
        const existing = s.queuedMessages[msg.conversation_id] ?? [];
        // Match by client id, else by first entry with the same text (covers
        // reconnect-rehydrated entries that lack the original id).
        let idx = msg.client_msg_id ? existing.findIndex((m) => m.id === msg.client_msg_id) : -1;
        if (idx < 0) idx = existing.findIndex((m) => m.text === msg.content);

        // Drop the promoted queue entry (if this client had one queued).
        const queuedMessages = { ...s.queuedMessages };
        if (idx >= 0) {
          const nextQueue = existing.filter((_, i) => i !== idx);
          if (nextQueue.length) queuedMessages[msg.conversation_id] = nextQueue;
          else delete queuedMessages[msg.conversation_id];
        }

        const isActive = msg.conversation_id === s.activeConversationId;
        if (!isActive) return { queuedMessages };

        // When there was no queued entry (idx < 0), this is either the run's
        // first directly-sent message on the window that SENT it — whose real
        // bubble already exists (added in ``sendMessage``, so it sits at the
        // tail) — or the same message arriving on ANOTHER of the user's windows
        // that never had a bubble for it. Distinguish by the tail: if it already
        // holds this exact user message, skip to avoid a duplicate; otherwise
        // create it so the other window shows the send live.
        if (idx < 0) {
          const last = s.messages[s.messages.length - 1];
          let lastText: string | null = null;
          if (last && last.role === "user") {
            try {
              const b = JSON.parse(last.content_json);
              if (Array.isArray(b) && b[0]?.type === "text") lastText = b[0].text as string;
            } catch { /* not a text bubble */ }
          }
          if (lastText === msg.content) return { queuedMessages };
        }

        // Append the real user bubble and reset per-run stream state, mirroring
        // the direct-send path.
        const userMsg: Message = {
          id: `temp-${Date.now()}`,
          role: "user",
          content_json: JSON.stringify([{ type: "text", text: msg.content }]),
          created_at: new Date().toISOString(),
        };
        return {
          queuedMessages,
          isAgentRunning: true,
          ...STREAM_RESET,
          toolCalls: new Map(),
          subagents: new Map(),
          messages: [...s.messages, userMsg],
          _scrollToBottomTick: s._scrollToBottomTick + 1,
        };
      });
      return;
    }

    // Durable sub-agent runs changed (ADR-0019). Server ships the whole row set
    // for the conversation, so the panel replaces rather than reconciles — a
    // client merging deltas is a client that can drift from the table, which is
    // the upstream bug where stopped tasks come back as "Running" on refresh.
    // Ignored for chats that aren't open: the panel only ever shows one.
    if (msg.type === "subagents_changed") {
      if (msg.conversation_id !== get().activeConversationId) return;
      set({ subagentRuns: msg.subagents ?? [] });
      return;
    }

    // A persisted system notice (finished-delegation receipt, ADR-0019):
    // append it to the open transcript LIVE — previously the row only showed
    // after a reload, because this frame had no handler at all. Idempotent by
    // message id, so a later transcript refresh de-dupes rather than doubles.
    if (msg.type === "system_notice") {
      if (msg.conversation_id !== get().activeConversationId) return;
      set((s) => {
        if (s.messages.some((m) => m.id === msg.message_id)) return s;
        return {
          messages: [
            ...s.messages,
            {
              id: msg.message_id,
              role: "system" as const,
              content_json: JSON.stringify([{ type: "text", text: msg.text }]),
              tool_name: msg.tool_name ?? undefined,
              tool_use_id: msg.tool_use_id ?? undefined,
              created_at: msg.created_at,
            },
          ],
        };
      });
      return;
    }

    // Conversation-LIST live push (Phase B2): another of the user's windows
    // created / renamed / pinned / changed a conversation. Upsert it into the
    // sidebar list — patch by id if present, else prepend. Idempotent, so the
    // originating window's own optimistic row de-dupes rather than doubling
    // (mirrors createConversation's prepend + loadConversations' dedup).
    if (msg.type === "conversation_upsert") {
      // Defensive: a tombstoned row is a deletion, never a sidebar row — drop it
      // instead of (re)adding it. The backend broadcasts conversation_deleted for
      // tombstones, but guarding here means a stray upsert can't resurrect a
      // deleted chat. (#70)
      if ((msg.conversation as { deleted_at?: string | null }).deleted_at) {
        set((s) => ({
          conversations: s.conversations.filter((c) => c.id !== msg.conversation.id),
          teamConversations: s.teamConversations.filter((c) => c.id !== msg.conversation.id),
        }));
        return;
      }
      set((s) => {
        const merged = mergeConversationRow(s, msg.conversation, msg.owner_id);
        // Durable running state (#79): keep the OPEN chat's spinner in step with
        // the row. Start: row says running and we don't — light it (and anchor
        // the timer to the true start). Stop: row says not-running — clear it,
        // unless a LOCAL task is live (local task_status is authoritative here)
        // or we just sent (the row can't reflect the send yet).
        if (msg.conversation.id === s.activeConversationId) {
          const rowRunning = !!msg.conversation.agent_running;
          if (rowRunning && !s.isAgentRunning) {
            const startedMs = msg.conversation.active_turn_started_at
              ? parseUTC(msg.conversation.active_turn_started_at).getTime()
              : null;
            return {
              ...merged,
              isAgentRunning: true,
              // Earliest anchor wins (the row carries the true start).
              turnStartedAt:
                startedMs != null &&
                startedMs < (s.turnStartedAt[msg.conversation.id] ?? Infinity)
                  ? { ...s.turnStartedAt, [msg.conversation.id]: startedMs }
                  : s.turnStartedAt,
            };
          }
          // The 8s send-guard protects against STALE pre-send rows blanking a
          // just-started run. But a cleared row stamped AFTER the send is not
          // stale — it's the turn (typically remote) genuinely finishing fast.
          // Without this bypass, a remote turn that completes inside the guard
          // window only clears via the ~8.5s recheck timer, so the spinner
          // lingered 10s+ after the reply had already rendered (found live).
          //
          // A durable clear stamped AFTER our send OUTRANKS ``runningTasks``.
          // Under ADR-0015 the lease is released in the same transaction as the
          // terminal ``result`` row, so `agent_running=0` on a row newer than
          // our send means the turn is definitively over — it is not a hint to
          // be second-guessed. A local entry that survives that is stale BY
          // CONSTRUCTION: it means the turn tail never returned to emit
          // ``task_status:false`` (a hung relay/persist-drain — the 2026-07-28
          // incident, seen again on cloud 2026-07-29 *after* the lease fix
          // landed: the row read idle within 700ms of the reply and the spinner
          // still ran). Obeying it pinned "Working" open forever, because every
          // later upsert was vetoed identically and ``scheduleRunningRecheck``
          // bailed on the same condition. So we PURGE the stale entry rather
          // than yield to it.
          //
          // ``runningTasks`` still wins when the row is NOT newer than our send
          // — that's the case it exists for (a stale pre-send row must never
          // blank a live local turn). A genuinely-live turn always holds a
          // lease, so its row reads running and never reaches here.
          const rowUpdatedMs = msg.conversation.updated_at
            ? parseUTC(msg.conversation.updated_at).getTime()
            : 0;
          // "Newer than the send" alone is NOT proof the send's turn finished:
          // mark-read and the lease-owner stamp bump `updated_at` between the
          // send and `begin_turn`, while the row still carries the PREVIOUS
          // turn's `active_turn_started_at`. Require the start stamp to have
          // advanced past the pre-send one — same server-clock field on both
          // sides, so client↔server skew can't flip the verdict. A genuinely
          // fast finish qualifies (its turn started after our send); a
          // pre-turn bump never does (2026-08-04 blanking-indicator report).
          const rowTurnStartMs = msg.conversation.active_turn_started_at
            ? parseUTC(msg.conversation.active_turn_started_at).getTime()
            : 0;
          const clearedAfterSend =
            rowUpdatedMs > (s.lastSendAt[msg.conversation.id] || 0) &&
            rowTurnStartMs > (s.preSendTurnStart[msg.conversation.id] || 0);
          const hasLocalTask = s.runningTasks.has(msg.conversation.id);
          const willClear =
            !rowRunning &&
            s.isAgentRunning &&
            (clearedAfterSend ||
              (!hasLocalTask &&
                Date.now() - (s.lastSendAt[msg.conversation.id] || 0) > 8000));
          if (!rowRunning && s.isAgentRunning) {
            console.debug(
              "[cowork-indicator]", willClear ? "CLEAR" : "suppress",
              msg.conversation.id.slice(0, 8),
              `rowUpdated=${rowUpdatedMs} rowTurnStart=${rowTurnStartMs}`,
              `lastSend=${s.lastSendAt[msg.conversation.id] || 0}`,
              `preSendTurnStart=${s.preSendTurnStart[msg.conversation.id] || 0}`,
              `clearedAfterSend=${clearedAfterSend} hasLocalTask=${hasLocalTask}`,
            );
          }
          if (willClear) {
            let runningTasks = s.runningTasks;
            if (hasLocalTask) {
              runningTasks = new Set(runningTasks);
              runningTasks.delete(msg.conversation.id);
            }
            return { ...merged, isAgentRunning: false, runningTasks };
          }
        }
        return merged;
      });
      // A suppressed/ignored not-running row must not be the LAST word — if a
      // remote turn finished inside the 8s guard window, no further upsert is
      // coming. Re-check the merged row once the window closes.
      if (!msg.conversation.agent_running && msg.conversation.id === get().activeConversationId) {
        scheduleRunningRecheck(msg.conversation.id);
      }
      return;
    }

    // A conversation was deleted in another window — drop it from the sidebar.
    // If it's the one open here, clear the view and go home (same handling as
    // deleteConversation, since the URL still resolves to the now-gone chat).
    if (msg.type === "conversation_deleted") {
      const wasActive = get().activeConversationId === msg.conversation_id;
      set((s) => {
        // Also drop any lingering running entry so a chat deleted mid-turn can't
        // sit in "Working" forever (#70).
        let runningTasks = s.runningTasks;
        if (runningTasks.has(msg.conversation_id)) {
          runningTasks = new Set(runningTasks);
          runningTasks.delete(msg.conversation_id);
        }
        return {
          conversations: s.conversations.filter((c) => c.id !== msg.conversation_id),
          teamConversations: s.teamConversations.filter((c) => c.id !== msg.conversation_id),
          runningTasks,
          activeConversationId: wasActive ? null : s.activeConversationId,
          messages: wasActive ? [] : s.messages,
          toolCalls: wasActive ? new Map() : s.toolCalls,
          isAgentRunning: wasActive ? false : s.isAgentRunning,
        };
      });
      if (wasActive) useNavStore.getState().navigate("/home");
      return;
    }

    // Real-time cloud→desktop sync (Phase B4): a background pull applied new
    // messages to this conversation (it ran on the cloud, not here). The sidebar
    // row already updated via conversation_upsert; if this is the OPEN chat,
    // refresh its tail so the new messages appear live without a reload. Fetches
    // strictly-newer messages from the persisted store and merges (dedup) — the
    // same shape as loadNewerMessages. No-op unless it's the active conversation.
    if (msg.type === "conversation_synced") {
      const convId = msg.conversation_id;
      if (get().activeConversationId !== convId) return;
      const newest = get().newestLoaded;
      void (async () => {
        try {
          const qs = newest
            ? `?after=${encodeURIComponent(newest)}&limit=${MESSAGE_PAGE_SIZE}`
            : `?limit=${MESSAGE_PAGE_SIZE}`;
          const page = await apiFetch<MessagesPage>(`/conversations/${convId}/messages${qs}`);
          set((s) => {
            if (s.activeConversationId !== convId || page.messages.length === 0) return s;
            const existingIds = new Set(s.messages.map((m) => m.id));
            const incoming = page.messages.filter((m) => !existingIds.has(m.id));
            if (incoming.length === 0) return s;
            // A message typed HERE but persisted elsewhere (remote dispatch,
            // or a queued send that finally ran) syncs back as a real row.
            // Drop its local optimistic twin so it doesn't double up: EXACT
            // match by the persisted ``client_msg_id`` (#84 — the optimistic
            // bubble's own id), falling back to the legacy text heuristic for
            // rows written by pre-v32 peers.
            const incomingUsers = incoming.filter((m) => m.role === "user");
            const incomingClientIds = new Set(
              incomingUsers.map((m) => m.client_msg_id).filter(Boolean) as string[],
            );
            const incomingUserTexts = new Set(
              incomingUsers.map((m) => firstText(m.content_json)).filter(Boolean),
            );
            const base = (incomingClientIds.size || incomingUserTexts.size)
              ? s.messages.filter((m) => {
                  if (m.role !== "user") return true;
                  if (incomingClientIds.has(m.id)) return false; // exact twin
                  return !(m.id.startsWith("temp-") && incomingUserTexts.has(firstText(m.content_json)));
                })
              : s.messages;
            const merged = [...base, ...incoming].sort(
              // parseUTC, not new Date() — see the openConversation sort comment.
          (a, b) => parseUTC(a.created_at).getTime() - parseUTC(b.created_at).getTime(),
            );
            return { messages: merged, newestLoaded: page.newest_loaded ?? s.newestLoaded };
          });
        } catch {
          // Transient — the 15s reconcile pull (and the next hint) will retry.
        }
      })();
      return;
    }

    // Portable sessions (Phase C): the turn wasn't run here — this conversation
    // is assigned to another environment AND that env is offline/unreachable, so
    // it couldn't be relayed. Surface a "runs on X — bring it here" prompt.
    if (msg.type === "execution_elsewhere") {
      if (get().activeConversationId === msg.conversation_id) {
        set({
          executionElsewhere: { conversationId: msg.conversation_id, location: msg.location },
          isAgentRunning: false,
        });
      }
      return;
    }

    // Remote dispatch (#70): the message WAS relayed to the owning env, which is
    // running it now — the reply syncs back. Keep the optimistic bubble and the
    // "Working" state: the owning env stamps the durable ``active_turn_*`` row,
    // which replicates down and re-lights the spinner via ``conversation_upsert``
    // (agent_running), and clears it when the remote turn ends. There is NO
    // cross-device ``task_status`` — the relaying backend only emits a *local*
    // ``task_status:false`` right before dispatch to purge any stale
    // ``runningTasks`` entry (so the row-driven clear isn't wedged); the
    // send-time ``staleStop`` guard keeps ``isAgentRunning`` lit across that
    // handoff. Here we just clear any stale "runs elsewhere" prompt. No strand.
    if (msg.type === "execution_dispatched") {
      if (get().activeConversationId === msg.conversation_id) {
        set((s) => ({
          executionElsewhere:
            s.executionElsewhere?.conversationId === msg.conversation_id ? null : s.executionElsewhere,
        }));
      }
      return;
    }

    // The server dropped the queued backlog (clear affordance / echo).
    if (msg.type === "queue_cleared") {
      set((s) => {
        if (!s.queuedMessages[msg.conversation_id]) return s;
        const queuedMessages = { ...s.queuedMessages };
        delete queuedMessages[msg.conversation_id];
        return { queuedMessages };
      });
      return;
    }

    if (msg.type === "job_complete") {
      const isActiveConversation = msg.conversation_id === activeId;
      const isForeground = !document.hidden && document.hasFocus();
      const shouldEscalateDesktop = !isActiveConversation || !isForeground;

      set((s) => {
        const next = new Set(s.runningTasks);
        next.delete(msg.conversation_id);
        return {
          runningTasks: next,
          ...(isActiveConversation ? { isAgentRunning: false } : {}),
        };
      });

      // Intent + preview come from the backend. Fall back to old-style
      // defaults for older server builds so the UI stays usable during
      // a partial deploy.
      const intent = msg.intent ?? (msg.status === "error" ? "error" : "response");
      const preview = msg.preview || msg.message;
      // In-app toast: any background completion, or any error (so the
      // user doesn't miss a failure in the chat they're watching).
      if (!isActiveConversation || msg.status === "error") {
        useToastStore.getState().push({
          intent,
          title: msg.conversation_title,
          preview,
          conversationId: msg.conversation_id,
        });
      }
      // OS notification: only when the window can't deliver the toast
      // itself (background or unfocused).
      if (shouldEscalateDesktop) {
        void triggerDesktopNotify({
          title: msg.conversation_title,
          message: preview,
          conversationId: msg.conversation_id,
          intent,
        });
      }
      return;
    }

    if (msg.type === "focus_conversation") {
      // Backend asked us to deep-link a specific chat — fired when the
      // user clicks an OS notification action. Raise the native window
      // (no-op in browser mode) and switch conversations.
      void raiseAppWindow();
      const target = msg.conversation_id;
      if (target && target !== get().activeConversationId) {
        void get().openConversation(target);
      }
      return;
    }

    if (msg.type === "open_deeplink") {
      // External deep link (nbyula.com "Delegate Task to OmniWerk"): PREFILL the
      // new-chat composer with the seed and let the operator review + send — do
      // NOT auto-send. Stash as pendingHomeSeed (HomeView consumes it; a direct
      // setHomeDraft would be wiped by HomeView's mount-clear) and navigate Home.
      void raiseAppWindow();
      const prompt = (msg.prompt ?? "").trim();
      if (prompt) {
        get().setPendingHomeSeed(prompt);
        useNavStore.getState().navigate("/home");
      }
      return;
    }

    if (msg.type === "cli_update") {
      // Launch-time Nbyula CLI auto-update (within the pinned band). Surface a
      // side toast: "found" uses the wording the user expects while the download
      // runs; "done" resolves it. The backend only emits "done" *after* a
      // "found", so done && !updated always means the announced swap didn't
      // land — show a low-key note rather than leaving the "found" toast
      // dangling (don't interpolate to_version there: on a failed swap it's the
      // OLD version, not the target).
      if (msg.phase === "found") {
        const ver = msg.to_version ? `v${msg.to_version}` : "a new version";
        useToastStore.getState().push({
          intent: "info",
          icon: "spinner",
          title: "Nbyula CLI",
          preview: `Updating to ${ver}…`,
        });
      } else if (msg.phase === "done") {
        useToastStore.getState().push(
          msg.updated
            ? {
                intent: "success",
                icon: "check",
                title: "Nbyula CLI",
                preview: `Updated to ${msg.to_version ? `v${msg.to_version}` : "the latest version"}`,
              }
            : {
                intent: "info",
                title: "Nbyula CLI",
                preview: "Update didn't finish — will retry next launch.",
              },
        );
      }
      return;
    }

    if (msg.type === "sync_progress") {
      // Cross-device backfill (ADR-0008). One updatable toast doubles as the
      // "CoWork is now in the cloud" note and a live progress readout.
      const toast = useToastStore.getState();
      if (msg.phase === "syncing") {
        const preview =
          msg.total && msg.total > 0
            ? `Syncing your workspace to the cloud… ${(msg.done ?? 0).toLocaleString()} / ${msg.total.toLocaleString()} messages`
            : "Syncing your workspace to the cloud…";
        const existing = get().syncToastId;
        if (existing) {
          toast.update(existing, { preview });
        } else {
          // Sticky (duration 0) until a terminal phase clears it.
          set({
            syncToastId: toast.push({
              intent: "info",
              icon: "spinner",
              title: "CoWork Cloud",
              preview,
              duration: 0,
            }),
          });
        }
      } else {
        const existing = get().syncToastId;
        if (existing) toast.remove(existing);
        set({ syncToastId: null });
        if (msg.phase === "done") {
          toast.push({
            intent: "success",
            icon: "check",
            title: "CoWork Cloud",
            preview:
              "Your workspace is now in the cloud — open omniwerk.nbyula.com in a browser, no need to re-enter anything.",
          });
        } else {
          // Interrupted mid-backfill. Progress is cursor-persisted server-side,
          // so "Continue sync" resumes from where it stopped (also auto-resumes
          // on the next launch if dismissed).
          toast.push({
            intent: "error",
            title: "CoWork Cloud",
            preview:
              "Cloud sync was interrupted — your progress is saved. Continue now, or it resumes on the next launch.",
            duration: 0,
            action: {
              label: "Continue sync",
              run: () => {
                void apiFetch("/sync/now", { method: "POST" }).catch(() => {});
              },
            },
          });
        }
      }
      return;
    }

    // Attention prompts (AskUserQuestion / tool approval) need to surface
    // a toast + OS notification for *background* conversations so the
    // user doesn't miss the agent blocking on them. Foreground/active
    // conversations already render the widget itself — no toast needed.
    if (msg.type === "user_question" || msg.type === "approval_request") {
      const isActiveConversation = msg.conversation_id === activeId;
      const isForeground = !document.hidden && document.hasFocus();
      if (!isActiveConversation) {
        const title =
          get().conversations.find((c) => c.id === msg.conversation_id)?.title
          ?? "Conversation";
        const intent = (msg.intent ?? (msg.type === "user_question" ? "question" : "approval")) as
          | "question"
          | "approval";
        const preview =
          msg.preview
          ?? (msg.type === "user_question"
            ? msg.questions[0]?.question ?? "The agent needs your input."
            : `Approve "${msg.action}" to continue.`);
        useToastStore.getState().push({
          intent,
          title,
          preview,
          conversationId: msg.conversation_id,
        });
        if (!isForeground || !isActiveConversation) {
          void triggerDesktopNotify({
            title,
            message: preview,
            conversationId: msg.conversation_id,
            intent,
          });
        }
      }
      // Fall through: when this event is for the active conversation
      // the case handler below still updates `pendingQuestions` /
      // `pendingApproval`. For background conversations we don't need
      // to update widget state — when the user switches to that chat
      // ``attach_ws`` will replay the blocking request.
    }

    // Bump the unread badge for background conversations as soon as the
    // agent starts producing a new assistant turn. We optimistically
    // increment per ``assistant_text`` (which fires once per assistant
    // message) — the server-side count gets reconciled on the next
    // loadConversations / open round-trip.
    if (msg.type === "assistant_text" && msg.conversation_id !== activeId) {
      set((s) => ({
        conversations: s.conversations.map((c) =>
          c.id === msg.conversation_id
            ? { ...c, unread_count: (c.unread_count ?? 0) + 1 }
            : c,
        ),
      }));
    }

    // All other events: only process if they belong to the active conversation
    if (msg.conversation_id !== activeId) return;

    switch (msg.type) {
      case "assistant_text": {
        const assistantMsg: Message = {
          id: msg.message_id,
          role: "assistant",
          content_json: JSON.stringify([{ type: "text", text: msg.text }]),
          // ``model`` arrives on the WS payload (mirrors the SQLite row
          // session_manager just wrote) so the badge shows on live-streamed
          // bubbles without waiting for the next REST history fetch.
          model: msg.model ?? null,
          created_at: new Date().toISOString(),
        };
        // The persisted block landed — drop the live draft so the text isn't
        // shown twice (footer draft + bubble), and mark this id as just-streamed
        // so the list skips its entry animation. Dedup against REST catch-up.
        set((s) => {
          const cleared = s.streamingText
            ? { streamingText: "", streamingBlock: null, lastStreamedMessageId: msg.message_id }
            : {};
          if (s.messages.some((m) => m.id === msg.message_id)) return cleared;
          return { ...cleared, messages: [...s.messages, assistantMsg] };
        });
        break;
      }

      case "thinking": {
        // Canonical thinking block persisted on the backend. Append it as a
        // ``thinking`` row (it lands just before ``assistant_text``, so it sits
        // above the answer in chronological order) and retire the transient
        // footer panel — the reasoning now lives as a durable row.
        const thinkingMsg: Message = {
          id: msg.message_id,
          role: "thinking",
          content_json: JSON.stringify([
            { type: "thinking", thinking: msg.text, duration_ms: msg.duration_ms ?? null },
          ]),
          created_at: new Date().toISOString(),
        };
        set((s) => {
          const clearedFooter = {
            thinkingText: "",
            thinkingDone: false,
            thinkingStartedAt: null,
            thinkingExpanded: true,
          };
          if (s.messages.some((m) => m.id === msg.message_id)) return clearedFooter;
          return { ...clearedFooter, messages: [...s.messages, thinkingMsg] };
        });
        break;
      }

      case "assistant_text_delta": {
        set((s) => {
          const base = s.streamingBlock === msg.block ? s.streamingText : "";
          // The answer has begun — collapse the thinking panel to "Thought".
          const collapse = s.thinkingText && !s.thinkingDone
            ? { thinkingDone: true, thinkingExpanded: false }
            : {};
          return { streamingText: base + msg.text, streamingBlock: msg.block, ...collapse };
        });
        break;
      }

      case "thinking_delta": {
        set((s) => {
          // A fresh thinking burst (first ever, or after a prior step's answer)
          // resets the panel; otherwise accumulate.
          const fresh = s.thinkingDone || !s.thinkingText;
          return {
            thinkingText: (fresh ? "" : s.thinkingText) + msg.text,
            thinkingDone: false,
            thinkingExpanded: fresh ? true : s.thinkingExpanded,
            thinkingStartedAt: fresh ? Date.now() : s.thinkingStartedAt,
          };
        });
        break;
      }

      case "tool_use_start": {
        const toolCall: ToolCallState = {
          tool_use_id: msg.tool_use_id,
          tool_name: msg.tool_name,
          input: msg.input,
          status: "running",
          started_at: Date.now(),
        };
        // Insert a tool_use message so it appears in chronological order.
        // Mirror the DB-persisted shape (see ``session_manager._save_message``)
        // so derived selectors that walk ``content_json`` — e.g. the Files
        // tab's ``extractOtherFiles`` — see the same structure live as on
        // reload. The bubble UI reads ``toolCalls`` for its inputs and
        // doesn't care about this field, but observers shouldn't have to.
        const toolMsg: Message = {
          id: `tool-${msg.tool_use_id}`,
          role: "tool_use",
          content_json: JSON.stringify([{
            type: "tool_use",
            id: msg.tool_use_id,
            name: msg.tool_name,
            input: msg.input,
          }]),
          tool_name: msg.tool_name,
          tool_use_id: msg.tool_use_id,
          created_at: new Date().toISOString(),
        };
        set((s) => {
          // A tool call ends the thinking step too — collapse to "Thought".
          const collapse = s.thinkingText && !s.thinkingDone
            ? { thinkingDone: true, thinkingExpanded: false }
            : {};
          if (s.messages.some((m) => m.id === toolMsg.id)) return { toolCalls: new Map(s.toolCalls).set(msg.tool_use_id, toolCall), ...collapse };
          const calls = new Map(s.toolCalls);
          calls.set(msg.tool_use_id, toolCall);
          return { toolCalls: calls, messages: [...s.messages, toolMsg], ...collapse };
        });
        break;
      }

      case "tool_result": {
        set((s) => {
          const calls = new Map(s.toolCalls);
          const existing = calls.get(msg.tool_use_id);
          if (existing) {
            calls.set(msg.tool_use_id, {
              ...existing,
              status: msg.is_error ? "error" : "success",
              result: msg.content,
              is_error: msg.is_error,
              duration_ms: Date.now() - existing.started_at,
            });
          }
          // Belt-and-suspenders: a Task tool_result also retires its live
          // sub-agent entry, in case the terminal ``subagent_progress`` frame
          // was dropped (no attached socket at that instant, etc.).
          if (s.subagents.has(msg.tool_use_id)) {
            const subs = new Map(s.subagents);
            subs.delete(msg.tool_use_id);
            return { toolCalls: calls, subagents: subs };
          }
          return { toolCalls: calls };
        });
        break;
      }

      case "subagent_progress": {
        set((s) => {
          const subs = new Map(s.subagents);
          // Terminal state → drop it so the live count falls.
          if (msg.status !== "running") {
            subs.delete(msg.tool_use_id);
            return { subagents: subs };
          }
          // Upsert. Progress ticks omit agent_type/description, so keep the
          // first non-null value we saw (TaskStarted carries them).
          const prev = subs.get(msg.tool_use_id);
          subs.set(msg.tool_use_id, {
            tool_use_id: msg.tool_use_id,
            conversation_id: msg.conversation_id,
            agent_type: msg.agent_type ?? prev?.agent_type ?? null,
            description: msg.description ?? prev?.description ?? null,
            last_tool_name: msg.last_tool_name ?? prev?.last_tool_name ?? null,
            total_tokens: msg.total_tokens || prev?.total_tokens || 0,
            tool_uses: msg.tool_uses || prev?.tool_uses || 0,
            duration_ms: msg.duration_ms || prev?.duration_ms || 0,
            status: "running",
            started_at: prev?.started_at ?? Date.now(),
          });
          return { subagents: subs };
        });
        break;
      }

      case "result": {
        const resultMsg: Message = {
          id: `result-${Date.now()}`,
          role: "result",
          content_json: JSON.stringify([{ type: "result", text: "" }]),
          cost_usd: msg.cost_usd,
          pplx_cost_usd: msg.pplx_cost_usd,
          duration_ms: msg.duration_ms,
          created_at: new Date().toISOString(),
        };
        set((s) => {
          const next = new Set(s.runningTasks);
          next.delete(msg.conversation_id);
          // Turn's done — drop this conversation's live sub-agents (scoped by
          // conversation_id so a background chat finishing can't wipe the
          // foreground's indicator).
          const subagents = new Map(
            [...s.subagents].filter(([, a]) => a.conversation_id !== msg.conversation_id),
          );
          // Only update context state if this result is for the
          // currently-open conversation — a background conversation
          // finishing should not steal the foreground's nudge state.
          const isActive = msg.conversation_id === s.activeConversationId;
          const contextUpdate = isActive && typeof msg.context_tokens === "number"
            ? {
                contextTokens: msg.context_tokens,
                contextPercent: typeof msg.context_percent === "number"
                  ? msg.context_percent
                  : computeContextPercent(msg.context_tokens, msg.context_model ?? s.selectedModel),
                contextLimit: getContextLimit(msg.context_model ?? s.selectedModel),
                contextModel: msg.context_model ?? s.selectedModel,
              }
            : {};
          // Cumulative token totals come through as a single snapshot
          // (not deltas) so applying them is an idempotent overwrite that
          // reconciles the live ``token_update`` deltas accumulated during
          // the run. Guard on a real number: the backend defaults
          // ``token_totals`` to ``{}`` on zero-token turns, and an empty
          // object is truthy — applying it would overwrite the live totals
          // with ``undefined``.
          const totalsUpdate = isActive && typeof msg.token_totals?.input_tokens === "number"
            ? {
                totalInputTokens: msg.token_totals.input_tokens,
                totalOutputTokens: msg.token_totals.output_tokens,
                totalCacheReadTokens: msg.token_totals.cache_read_tokens,
                totalCacheCreationTokens: msg.token_totals.cache_creation_tokens,
              }
            : {};
          // Per-model breakdown — authoritative cumulative snapshot, so
          // *set* it (overwrite the live token_update accumulation). The
          // backend defaults this to ``{}`` on a no-token turn, and an empty
          // object is truthy — applying it would wipe the accumulated map, so
          // guard on a non-empty snapshot (same spirit as the token_totals
          // number-guard above).
          const modelUsageUpdate = isActive
            && msg.model_token_usage
            && Object.keys(msg.model_token_usage).length > 0
            ? { modelTokenUsage: msg.model_token_usage }
            : {};
          // The CLI's context reading for the turn that just ended. Absent when
          // it couldn't answer — keep the previous one rather than blanking the
          // panel (the backend persists the same way).
          const contextBreakdownUpdate = isActive && msg.context_breakdown
            ? { contextBreakdown: msg.context_breakdown }
            : {};
          // Cumulative cost snapshot — same idempotent-overwrite logic as
          // the token totals. ``cost_totals`` defaults to ``{}`` on turns
          // with no usage, so guard on a real number before applying.
          const costUpdate = isActive && typeof msg.cost_totals?.cost_usd === "number"
            ? {
                totalCostUsd: msg.cost_totals.cost_usd,
                totalPplxCostUsd: msg.cost_totals.pplx_cost_usd,
              }
            : {};
          // Advance the server-side ``last_read_at`` for the active
          // conversation now that this turn is done — otherwise a
          // reload would re-light the sidebar badge for a chat the
          // user has been watching all along.
          if (isActive) {
            void get().markConversationRead(msg.conversation_id);
          }
          // Mirror the resolved model into ``effectiveModel`` whenever the
          // backend reports one. This is how the Auto user learns which
          // provider just ran, without a separate round-trip.
          const effectiveUpdate = isActive && msg.context_model
            ? { effectiveModel: msg.context_model }
            : {};
          return {
            messages: [...s.messages, resultMsg],
            isAgentRunning: false,
            ...STREAM_RESET,
            evaluatorState: null,
            runningTasks: next,
            subagents,
            ...contextUpdate,
            ...totalsUpdate,
            ...modelUsageUpdate,
            ...contextBreakdownUpdate,
            ...costUpdate,
            ...effectiveUpdate,
          };
        });

        break;
      }

      case "compact": {
        // Compaction boundary — the Claude Agent SDK auto-compacted at the
        // context ceiling, or the user fired ``/compact``. Compaction is
        // harness-owned; we just reflect it by zeroing the local context view
        // so the occupancy bar drops (the next ``result`` repopulates it with
        // the post-compact size).
        set((s) => {
          if (msg.conversation_id !== s.activeConversationId) return s;
          return {
            contextTokens: 0,
            contextPercent: 0,
          };
        });
        useToastStore.getState().add(
          msg.trigger === "manual"
            ? "Chat compacted — older turns summarised to free up context."
            : "Chat was auto-compacted to free up context.",
          "info",
        );
        break;
      }

      case "shortlist_update": {
        // Direct store call instead of CustomEvent bridge
        useShortlistStore.getState().setLiveData(msg.data);
        // Auto-open right panel to shortlist tab
        set({ rightPanelOpen: true, rightPanelTab: "shortlist" });

        // Process artifacts bundled with the shortlist (XLSX etc.)
        const bundledArtifacts = (msg.artifacts || []) as Array<{ id: string; filename: string; file_type: string }>;
        if (bundledArtifacts.length > 0) {
          const artifactMsgs: Message[] = bundledArtifacts.map((a) => ({
            id: `artifact-${a.id}`,
            role: "artifact" as const,
            content_json: JSON.stringify([{ type: "artifact", artifact_id: a.id, filename: a.filename, file_type: a.file_type }]),
            created_at: new Date().toISOString(),
          }));
          set((s) => {
            const existingIds = new Set(s.artifacts.map((x) => x.id));
            const merged = [...s.artifacts];
            for (const a of bundledArtifacts) {
              if (!existingIds.has(a.id)) merged.push({ ...a, created_at: new Date().toISOString() });
            }
            const existingMsgIds = new Set(s.messages.map((m) => m.id));
            const msgs = [...s.messages, ...artifactMsgs.filter((m) => !existingMsgIds.has(m.id))];
            return { artifacts: merged, messages: msgs };
          });
        }

        useShortlistStore.setState((s) => {
          const exists = s.gallery.some((g) => g.id === msg.shortlist_id);
          if (!exists) {
            return {
              gallery: [{
                id: msg.shortlist_id,
                candidate_name: msg.data.candidate_name,
                version: 1,
                updated_at: new Date().toISOString(),
              }, ...s.gallery],
            };
          }
          return {};
        });
        break;
      }

      case "evaluator_running":
        set({ evaluatorState: { status: "running", startedAt: Date.now() } });
        break;

      case "evaluator_result": {
        const rerunning = !msg.passed && msg.issues?.some((i: { severity: string }) => i.severity === "critical");
        set({
          evaluatorState: {
            status: msg.passed ? "passed" : "failed",
            issues: msg.issues,
            rerunning: !!rerunning,
            startedAt: get().evaluatorState?.startedAt ?? Date.now(),
          },
        });
        // Clear after a delay if passed (the block will fade)
        if (msg.passed) {
          setTimeout(() => set({ evaluatorState: null }), 4000);
        }
        // If rerunning, the next evaluator_running will reset the state
        break;
      }

      case "approval_request":
        set({
          pendingApproval: { action: msg.action, description: msg.description, request_id: msg.request_id },
          isAgentRunning: false,
        });
        break;

      case "user_question":
        set({
          pendingQuestions: msg.questions || [],
          pendingQuestionRequestId: msg.request_id ?? null,
          isAgentRunning: false,
        });
        break;

      case "token_update": {
        // Live per-message token accounting during a run. Deltas accumulate
        // onto the running totals so the counter ticks up turn-by-turn; the
        // authoritative ``token_totals`` snapshot in the ``result`` event
        // reconciles any drift at end-of-run (it overwrites, not adds). Only
        // the active conversation's counter moves — a background run must not
        // shift the foreground numbers.
        if (msg.conversation_id !== activeId) break;
        set((s) => {
          const ctx = typeof msg.context_tokens === "number"
            ? {
                contextTokens: msg.context_tokens,
                contextPercent: typeof msg.context_percent === "number"
                  ? msg.context_percent
                  : computeContextPercent(msg.context_tokens, msg.context_model ?? s.selectedModel),
                contextLimit: getContextLimit(msg.context_model ?? s.selectedModel),
                contextModel: msg.context_model ?? s.selectedModel,
              }
            : {};
          // Accumulate this call's delta onto the per-model breakdown too, so
          // the model-wise view ticks up live. Keyed by the model that ran
          // (falls back to the active selection) and tagged with the provider
          // the backend reported. The ``result`` snapshot overwrites this with
          // the authoritative cumulative map, so any drift is transient.
          const modelKey = msg.context_model ?? s.selectedModel ?? "unknown";
          const prevEntry = s.modelTokenUsage[modelKey];
          const modelUsage: ModelTokenUsage = {
            ...s.modelTokenUsage,
            [modelKey]: {
              provider: msg.provider ?? prevEntry?.provider ?? "unknown",
              input_tokens: (prevEntry?.input_tokens ?? 0) + (msg.input_delta ?? 0),
              output_tokens: (prevEntry?.output_tokens ?? 0) + (msg.output_delta ?? 0),
              cache_read_tokens: (prevEntry?.cache_read_tokens ?? 0) + (msg.cache_read_delta ?? 0),
              cache_creation_tokens:
                (prevEntry?.cache_creation_tokens ?? 0) + (msg.cache_creation_delta ?? 0),
            },
          };
          return {
            totalInputTokens: s.totalInputTokens + (msg.input_delta ?? 0),
            totalOutputTokens: s.totalOutputTokens + (msg.output_delta ?? 0),
            totalCacheReadTokens: s.totalCacheReadTokens + (msg.cache_read_delta ?? 0),
            totalCacheCreationTokens: s.totalCacheCreationTokens + (msg.cache_creation_delta ?? 0),
            modelTokenUsage: modelUsage,
            ...ctx,
          };
        });
        break;
      }

      case "todos_updated": {
        const incoming = (msg.todos || []) as AgentTodo[];
        // Replace — TodoWrite always emits the full list, so diffing is
        // unnecessary. An empty array legitimately means "no active plan".
        set((s) => {
          // Surface the plan the first time the agent produces one. The
          // plan now lives in the right panel's "Plan" tab (it used to be
          // pinned at the top of the chat), so pop the panel open on that
          // tab when a plan first appears and the panel is closed —
          // preserving the old at-a-glance visibility. Never override a
          // panel the user already has open on another tab.
          if (incoming.length > 0 && s.todos.length === 0 && !s.rightPanelOpen) {
            return { todos: incoming, rightPanelOpen: true, rightPanelTab: "plan" as const };
          }
          return { todos: incoming };
        });
        break;
      }

      case "artifacts_update": {
        // Agent produced files (XLSX etc.) — add to artifacts list + show cards in chat
        const artifactsList = (msg.artifacts || []) as Array<{ id: string; filename: string; file_type: string }>;
        const newMsgs: Message[] = [];
        for (const artifact of artifactsList) {
          newMsgs.push({
            id: `artifact-${artifact.id}`,
            role: "artifact",
            content_json: JSON.stringify([{
              type: "artifact",
              artifact_id: artifact.id,
              filename: artifact.filename,
              file_type: artifact.file_type,
            }]),
            created_at: new Date().toISOString(),
          });
        }
        set((s) => {
          // Merge into artifacts list (deduplicate by id)
          const existingIds = new Set(s.artifacts.map((a) => a.id));
          const merged = [...s.artifacts];
          for (const a of artifactsList) {
            if (!existingIds.has(a.id)) {
              merged.push({ ...a, created_at: new Date().toISOString() });
            }
          }
          // Add message cards (deduplicate)
          const existingMsgIds = new Set(s.messages.map((m) => m.id));
          const msgs = [...s.messages, ...newMsgs.filter((m) => !existingMsgIds.has(m.id))];
          return { artifacts: merged, messages: msgs };
        });
        break;
      }

      case "genui": {
        // New generative UI widget — add a genui message and track the HTML
        const genuiMsg: Message = {
          id: msg.genui_id,
          role: "genui",
          content_json: JSON.stringify([{
            type: "genui",
            html: msg.html,
            title: msg.title || "",
            file_path: msg.file_path || "",
            genui_id: msg.genui_id,
          }]),
          created_at: new Date().toISOString(),
        };
        set((s) => {
          if (s.messages.some((m) => m.id === msg.genui_id)) return s;
          const nextHtml = new Map(s.genuiHtml);
          nextHtml.set(msg.genui_id, msg.html);
          return { messages: [...s.messages, genuiMsg], genuiHtml: nextHtml };
        });
        break;
      }

      case "genui_update": {
        // Auto-refresh: update HTML for an existing genui iframe in-place
        set((s) => {
          const nextHtml = new Map(s.genuiHtml);
          nextHtml.set(msg.genui_id, msg.html);
          return { genuiHtml: nextHtml };
        });
        break;
      }

      case "force_logout": {
        console.warn("Force logout:", msg.reason, msg.message);
        // Don't clear tokens or redirect — show a non-dismissable modal
        // so the user can re-login while the agent keeps running.
        set({ isAgentRunning: false, wsStatus: "session_expired" });
        break;
      }

      case "error": {
        console.error("Agent error:", msg.message);
        set((s) => ({
          isAgentRunning: false,
          ...STREAM_RESET,
          subagents: new Map(
            [...s.subagents].filter(([, a]) => a.conversation_id !== msg.conversation_id),
          ),
        }));
        const errMsg: Message = {
          id: `err-${Date.now()}`,
          role: "system",
          content_json: JSON.stringify([{ type: "text", text: `Error: ${msg.message}` }]),
          created_at: new Date().toISOString(),
        };
        set((s) => ({ messages: [...s.messages, errMsg] }));
        useToastStore.getState().add(msg.message, "error");
        break;
      }

      // The backend resolved Auto to a specific model, or the running
      // agent called ``switch_model``. Two stores need updating:
      //   - ``effectiveModel``: which model Auto actually landed on. Not
      //     rendered on the picker (that reads plain "Auto"; the resolved
      //     model is on each message bubble) — it feeds the Context panel
      //     and the cooldown-aware switch lock. Always set.
      //   - ``selectedModel``: what the next ``user_message`` WS event
      //     carries. ONLY updated when the agent (not Auto resolution)
      //     drove the switch — otherwise we'd clobber the user's
      //     dropdown pick on every Auto-resolved turn.
      // Without the ``selectedModel`` update for agent switches, the
      // next user message would carry the old model and the backend
      // would clobber the agent's persisted pick during ``handle_message``,
      // making the switch silently revert. We hit exactly that bug:
      // agent switched to gemini-2-5-flash-lite, next turn still ran on
      // claude-sonnet-4-6 because the dropdown didn't update.
      case "model_switched": {
        // The model is per-conversation, so a switch only touches the picker
        // when it belongs to the chat the user is viewing. A background
        // conversation resolving Auto or self-switching must never move the
        // active chat's selector. (The backend has already persisted the
        // agent's switch to that conversation's ``selected_model``, so its
        // own selector will be correct when the user opens it.)
        if (msg.conversation_id !== get().activeConversationId) break;
        const isAgent = msg.source === "agent";
        const isConcreteModel = !!msg.model && msg.model !== "auto";
        set({ effectiveModel: msg.model });
        if (isAgent && isConcreteModel) {
          set({
            selectedModel: msg.model,
            contextLimit: getContextLimit(msg.model),
          });
        } else if (isAgent && msg.model === "auto") {
          // The agent handed routing back to Auto (e.g. switch_model "auto").
          // Sync the picker to "auto" so it doesn't stay locked on the
          // previously-pinned model — otherwise the next user message re-sends
          // that stale model and silently clobbers the switch back to Auto.
          set({ selectedModel: "auto", contextLimit: getContextLimit("auto") });
        }
        // Inline marker (replaces the old toast). The backend already wrote the
        // durable ``model_switch`` row and deduped, so an event here always
        // names a genuine change — render it at the current point in the
        // transcript. Skip only the degenerate case where the previous message
        // is already a marker for the same model (a duplicate/replayed event).
        if (isConcreteModel) {
          set((s) => {
            const last = s.messages[s.messages.length - 1];
            if (last?.role === "model_switch") {
              try {
                const lb = JSON.parse(last.content_json)?.[0];
                if (lb?.model === msg.model) return s;
              } catch { /* fall through and append */ }
            }
            const switchMsg = buildModelSwitchMessage({
              model: msg.model,
              source: msg.source,
              by: msg.by || "Auto Router",
              provider: msg.provider,
              reason: msg.reason,
              id: msg.message_id,
              created_at: msg.created_at,
            });
            return { messages: [...s.messages, switchMsg] };
          });
        }
        break;
      }

      // A conversation was moved between execution environments (cloud ⇄ a
      // device). Render the inline divider at the current point in the open
      // chat's transcript — the backend already persisted the durable
      // ``env_switch`` row (and it rides the cross-device forwarder), so this
      // just shows it instantly. Only touches the chat the user is viewing;
      // a background conversation's move stays silent until opened (its row is
      // already persisted). Deduped by message id against a replayed event.
      case "env_switched": {
        if (msg.conversation_id !== get().activeConversationId) break;
        set((s) => {
          if (s.messages.some((m) => m.id === msg.message_id)) return s;
          const switchMsg = buildEnvSwitchMessage({
            from: msg.from,
            to: msg.to,
            from_label: msg.from_label,
            to_label: msg.to_label,
            by: msg.by,
            id: msg.message_id,
            created_at: msg.created_at,
          });
          return { messages: [...s.messages, switchMsg] };
        });
        break;
      }

      // A provider just failed a request hard enough that we cooled it
      // down. Cache the entry so the selector can show a warning pill
      // and so the next Auto pick (computed server-side) skips it.
      case "provider_cooled_down": {
        // Failover replays this turn on another provider; drop any draft from
        // the failed provider so the retry's deltas don't append to it.
        set((s) => ({
          ...STREAM_RESET,
          cooledDownProviders: {
            ...s.cooledDownProviders,
            [msg.provider]: {
              kind: msg.kind,
              reason: msg.reason,
              ts: Date.now(),
            },
          },
        }));
        const labels: Record<string, string> = {
          rate_limit: "rate-limited",
          overloaded: "overloaded",
          timeout: "timed out",
          auth: "auth failure",
          credit: "out of credits",
        };
        // Prefer the server's ``reason`` — it's the specific, truthful sentence
        // ("Claude usage limit reached — resets 16:20"). The generic label was
        // printing "rate-limited" for states that hadn't actually been reached.
        const what = labels[msg.kind] ?? msg.kind;
        useToastStore.getState().add(
          msg.reason
            ? `${msg.reason} — switched providers to keep going`
            : `${msg.provider} ${what} — switched providers to keep going`,
          "info",
        );
        break;
      }
    }
  },

  updateConversation: async (id, updates) => {
    await apiFetch(`/conversations/${id}`, { method: "PATCH", body: JSON.stringify(updates) });
    set((s) => ({
      conversations: s.conversations.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    }));
  },

  togglePinConversation: async (id) => {
    const current = get().conversations.find((c) => c.id === id);
    if (!current) return;
    const willPin = !current.pinned_at;
    // Optimistic update — server will stamp the authoritative timestamp
    // and we reconcile with the response. Failure rolls back.
    const optimistic = willPin ? new Date().toISOString() : null;
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === id ? { ...c, pinned_at: optimistic } : c,
      ),
    }));
    try {
      const res = await apiFetch<{ ok: boolean; pinned_at?: string | null }>(
        `/conversations/${id}`,
        { method: "PATCH", body: JSON.stringify({ pinned: willPin }) },
      );
      if (res && "pinned_at" in res) {
        const authoritative = res.pinned_at ?? null;
        set((s) => ({
          conversations: s.conversations.map((c) =>
            c.id === id ? { ...c, pinned_at: authoritative } : c,
          ),
        }));
      }
    } catch (err) {
      // Roll back to the prior state on failure.
      set((s) => ({
        conversations: s.conversations.map((c) =>
          c.id === id ? { ...c, pinned_at: current.pinned_at ?? null } : c,
        ),
      }));
      throw err;
    }
  },

  markConversationRead: async (id) => {
    const convId = id ?? get().activeConversationId;
    if (!convId) return;
    // An admin reading someone else's chat is SNIFFING, not reading it for
    // them: their unread state is the owner's, and marking it read here would
    // hide messages the owner has never seen. The endpoint is owner-scoped and
    // already answers 404, so this only stops a request guaranteed to fail —
    // but it makes the intent explicit rather than leaving correctness to a
    // server-side accident.
    if (get().teamConversations.some((c) => c.id === convId)) return;
    // Optimistic local update so the badge clears immediately even if
    // the POST is still in flight or fails. Server is authoritative on
    // the next list refresh.
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === convId ? { ...c, unread_count: 0 } : c,
      ),
    }));
    try {
      const res = await apiFetch<{ ok: boolean; last_read_at: string }>(
        `/conversations/${convId}/mark-read`,
        { method: "POST" },
      );
      if (res?.last_read_at) {
        set((s) => ({
          conversations: s.conversations.map((c) =>
            c.id === convId ? { ...c, last_read_at: res.last_read_at } : c,
          ),
        }));
      }
    } catch {
      // Best-effort — leave the optimistic badge clear in place. A
      // subsequent loadConversations will reconcile if the POST failed.
    }
  },

  deleteConversation: async (id) => {
    const wasActive = get().activeConversationId === id;
    await apiFetch(`/conversations/${id}`, { method: "DELETE" });
    set((s) => ({
      conversations: s.conversations.filter((c) => c.id !== id),
      activeConversationId: wasActive ? null : s.activeConversationId,
      messages: wasActive ? [] : s.messages,
      toolCalls: wasActive ? new Map() : s.toolCalls,
      isAgentRunning: wasActive ? false : s.isAgentRunning,
    }));
    // Deleting the open conversation must also leave its URL. The view is
    // URL-routed (`/chat/:id` → ChatView); clearing `activeConversationId`
    // alone leaves ChatView stranded on its loading shimmer because the URL
    // still resolves to the now-deleted chat. Send the user home instead.
    if (wasActive) {
      useNavStore.getState().navigate("/home");
    }
  },

  setHomeDraft: (text) => {
    set({ homeDraft: text });
  },
  setPendingHomeSeed: (text) => {
    set({ pendingHomeSeed: text });
  },

  addFiles: (files) => {
    set((s) => ({ attachedFiles: [...s.attachedFiles, ...files] }));
  },

  removeFile: (index) => {
    set((s) => ({ attachedFiles: s.attachedFiles.filter((_, i) => i !== index) }));
  },

  clearFiles: () => {
    set({ attachedFiles: [] });
  },
}));

// ─── Turn timing ────────────────────────────────────────────────────────
// Maintain ``turnStartedAt`` per conversation in one place instead of threading
// it through the ~10 sites that flip the running flags. A conversation counts as
// "running" if it's in the server-confirmed ``runningTasks`` set OR it's the
// active chat with ``isAgentRunning`` (optimistic, covers the local-send window
// before the server echoes). We stamp a conversation's start only when it is
// *newly* running with no existing anchor, and clear it when it stops running.
//
// Anchoring to that union — rather than the global ``isAgentRunning`` — is what
// fixes the timer resetting on chat switch: switching flips ``isAgentRunning``
// and ``activeConversationId`` but leaves ``runningTasks`` untouched, so a
// confirmed-running conversation stays in the union across the switch and keeps
// its original anchor instead of being cleared and re-stamped at 0s.
//
// For the narrow re-stamp case (a locally-sent turn not yet server-confirmed,
// switched away and back within the cold-start window) we recover the true
// start from ``lastSendAt`` when it's recent, else fall back to now. The guard
// (running inputs unchanged) makes the re-entrant notification from the setState
// below a no-op, so there is no loop.
function runningConvs(s: ChatState): Set<string> {
  const set = new Set(s.runningTasks);
  if (s.isAgentRunning && s.activeConversationId) set.add(s.activeConversationId);
  return set;
}
useChatStore.subscribe((state, prev) => {
  if (
    state.runningTasks === prev.runningTasks &&
    state.isAgentRunning === prev.isAgentRunning &&
    state.activeConversationId === prev.activeConversationId
  ) {
    return;
  }
  const now = runningConvs(state);
  const before = runningConvs(prev);
  let started = state.turnStartedAt;
  let changed = false;
  const stamp = (id: string, value: number) => {
    if (!changed) { started = { ...started }; changed = true; }
    started[id] = value;
  };
  for (const id of now) {
    if (before.has(id) || started[id] != null) continue;
    const sent = state.lastSendAt[id];
    stamp(id, sent && Date.now() - sent < 8000 ? sent : Date.now());
  }
  for (const id of before) {
    if (now.has(id) || started[id] == null) continue;
    if (!changed) { started = { ...started }; changed = true; }
    delete started[id];
  }
  if (changed) useChatStore.setState({ turnStartedAt: started });
});

/** Live sub-agents running for the *active* conversation — the "Working"
 *  indicator's count + activity lines read this. Filtered by conversation_id so
 *  a background chat's sub-agents never bleed into the foreground view. Selects
 *  the (stable) Map reference and derives via ``useMemo`` so it doesn't churn
 *  React on unrelated store updates. */
/**
 * Is a turn in flight on ``convId``, from every signal we have?
 *
 * The union of the durable row and the local task set, and it needs both:
 *
 *  * the **row** (``agent_running``) is the only signal that knows about a turn
 *    running on the OTHER environment. Keying a guard on ``runningTasks`` alone
 *    left the environment switcher enabled for exactly that case — a chat
 *    running on the user's laptop looked idle from the cloud UI, which is the
 *    scenario where moving it does the most damage.
 *  * the **local set** covers the sliver before the lease row exists (the send
 *    is optimistic; the row lands a beat later).
 *
 * Mirrors the server-side ``conversation_store.turn_in_flight`` predicate, so a
 * control the UI leaves enabled is not one the API is about to refuse.
 */
export function useConversationIsRunning(convId: string | null): boolean {
  const fromTasks = useChatStore((s) => (convId ? s.runningTasks.has(convId) : false));
  const fromRow = useChatStore((s) => {
    if (!convId) return false;
    const row =
      s.conversations.find((c) => c.id === convId) ??
      s.teamConversations.find((c) => c.id === convId);
    return !!row?.agent_running;
  });
  return fromTasks || fromRow;
}

export function useRunningSubagents(): SubagentInfo[] {
  const subagents = useChatStore((s) => s.subagents);
  const activeId = useChatStore((s) => s.activeConversationId);
  return useMemo(
    () =>
      [...subagents.values()].filter(
        (a) => a.conversation_id === activeId && a.status === "running",
      ),
    [subagents, activeId],
  );
}
